import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ESTADOS_PLANTILLA,
  ETIQUETA_ESTADO_PLANTILLA,
  ETIQUETA_TIPO_CAMPO_PROCEDIMIENTO,
  ETIQUETA_TIPO_ITEM_PLANTILLA,
  ETIQUETA_TIPO_TRABAJO,
  MAX_CAMPOS_PLANTILLA,
  RESPUESTAS_TERNARIAS,
  RESULTADOS_INSPECCION,
  TIPOS_CAMPO_PROCEDIMIENTO,
  TIPOS_TRABAJO,
  canonicoDiscreto,
  formatearFechaHora,
  normalizarValorCondicion,
  operadoresCondicionPara,
  valoresDiscretosCondicion,
  type Condicion,
  type EjecucionResumida,
  type EstadoPlantilla,
  type ItemPlantilla,
  type OperadorCondicion,
  type Pagina,
  type TipoCampoProcedimiento,
  type TipoTrabajo,
} from '@cmms/shared';
import { api, consulta, ErrorApi } from '../servicios/api.js';
import { ErroresFormulario } from '../componentes/piezas.js';
import { IndiceSecciones } from '../componentes/indice-secciones.js';
import { useSesion } from '../servicios/sesion.js';
import { useToasts } from '../componentes/notificaciones-globales.js';
import { BotonVolver } from '../componentes/boton-volver.js';
import { BotonImportarCsv } from '../componentes/importar-csv.js';
import { IconoArchivo, IconoArrastrar, IconoCalendario, IconoCondicional, IconoEliminar, IconoGaleria, IconoLupa, IconoMas, IconoOpciones } from '../componentes/iconos.js';
import {
  Aviso,
  Cargando,
  Dialogo,
  Etiqueta,
  GaleriaImagenes,
  Indicador,
  Paginacion,
  SeleccionMultiple,
  SubidaImagenes,
  Vacio,
  type AdjuntoImagen,
} from '../componentes/piezas.js';
import {
  SelectorActivo,
  SelectorFecha,
  SelectorLista,
  SelectorListaMultiple,
  SelectorUbicacion,
  useGruposOpciones,
  useTiposActivo,
  useTiposFalla,
  useUbicaciones,
} from '../componentes/selectores.js';
import { useEsMovil } from '../servicios/responsive.js';
import { EnlaceRuta } from '../componentes/enlace-ruta.js';

/**
 * Forma del ítem tal como vive en el editor: igual a `ItemPlantilla` salvo
 * `imagenesReferencia`, que aquí es la lista completa (id + url + nombre) que
 * necesita `SubidaImagenes` para mostrar miniaturas, en vez de solo los ids
 * que espera el contrato de guardado.
 */
interface ItemArbol extends Omit<ItemPlantilla, 'imagenesReferencia' | 'hijos'> {
  imagenesReferencia: AdjuntoImagen[];
  hijos: ItemArbol[];
}

/** Al servidor solo le importan los ids de las imágenes de referencia. */
function aEntrada(items: ItemArbol[]): ItemPlantilla[] {
  return items.map((item) => ({
    ...item,
    // El editor de opciones deja una fila vacía al final para seguir
    // escribiendo (§ pedido): si nunca se completó, no es una opción real.
    opciones: item.opciones.map((o) => o.trim()).filter(Boolean),
    imagenesReferencia: item.imagenesReferencia.map((a) => a.id),
    hijos: aEntrada(item.hijos),
  }));
}

/**
 * El servidor ya resuelve las imágenes de referencia a objetos {id,url,
 * nombreArchivo}; se completan los campos que `AdjuntoImagen` espera además
 * (con `entidadId` no nulo, para que `SubidaImagenes` sepa que ya están
 * vinculadas y no las borre del todo al quitarlas antes de guardar).
 */
function desdeLectura(items: (Omit<ItemPlantilla, 'imagenesReferencia' | 'hijos'> & {
  id: string;
  imagenesReferencia: { id: string; url: string; nombreArchivo: string }[];
  hijos: unknown[];
})[]): ItemArbol[] {
  return items.map((item) => {
    const arbol: ItemArbol = {
      ...item,
      imagenesReferencia: item.imagenesReferencia.map((img) => ({
        id: img.id,
        nombreArchivo: img.nombreArchivo,
        tipoMime: '',
        tamanoBytes: 0,
        entidad: 'ITEM_PLANTILLA',
        entidadId: item.id,
        creadoEl: '',
        url: img.url,
        origenSolicitud: false,
      })),
      hijos: desdeLectura(item.hijos as Parameters<typeof desdeLectura>[0]),
    };
    // Condiciones guardadas antes de cambiar el disparador: se ajustan al cargar
    // para que no queden valores que ya no existen.
    arbol.hijos = arbol.hijos.map((hijo) => ({ ...hijo, condicion: ajustarCondicion(hijo.condicion, arbol) }));
    return arbol;
  });
}

interface Plantilla {
  id: string;
  numero: number;
  nombre: string;
  descripcion: string | null;
  categoriaId: string | null;
  categoria: string | null;
  puntuacionActiva: boolean;
  version: number;
  estado: EstadoPlantilla;
  cantidadCampos: number;
  tieneCambiosSinPublicar: boolean;
  publicadoEl: string | null;
  ejecuciones: number;
  tipoTrabajo: TipoTrabajo | null;
  tiposFalla: string[];
  tiposActivo: string[];
  activos: string[];
  ubicaciones: string[];
  grupos: string[];
}

const TONO_ESTADO: Record<EstadoPlantilla, 'gris' | 'verde' | 'ambar'> = {
  BORRADOR: 'ambar',
  PUBLICADA: 'verde',
  ARCHIVADA: 'gris',
};

// ─── Biblioteca (§8.10) ──────────────────────────────────────────────────────

export function BibliotecaProcedimientos(): JSX.Element {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const esMovil = useEsMovil();
  const cliente = useQueryClient();
  const [estado, setEstado] = useState('');
  const [buscar, setBuscar] = useState('');
  // Filtro de conveniencia, no de acceso (§ pedido): acota a los procedimientos
  // a cargo de ciertos grupos. Arranca vacío (=todos); es el mismo dato
  // "Grupos a cargo" que ya trae cada plantilla, solo que filtrado acá en el
  // cliente, sin tocar el backend.
  const [grupoId, setGrupoId] = useState<string[]>([]);
  const [modalFiltrosAbierto, setModalFiltrosAbierto] = useState(false);
  const [creandoAbierto, setCreandoAbierto] = useState(false);
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [activoNuevoId, setActivoNuevoId] = useState('');
  const [tipoNuevo, setTipoNuevo] = useState<TipoTrabajo | ''>('');

  const { data: todasLasPlantillas, isLoading } = useQuery({
    queryKey: ['procedimientos', estado, buscar],
    queryFn: () => api.get<Plantilla[]>(`/procedimientos${consulta({ estado, buscar })}`),
  });
  const { data: grupos } = useGruposOpciones();
  const data =
    grupoId.length === 0
      ? todasLasPlantillas
      : todasLasPlantillas?.filter((p) => p.grupos.some((g) => grupoId.includes(g)));

  function limpiarFormularioNuevo(): void {
    setCreandoAbierto(false);
    setNombreNuevo('');
    setActivoNuevoId('');
    setTipoNuevo('');
  }

  // §8.2: se pide el nombre antes de crear, en vez de crearla ya con un
  // nombre por defecto — con varios borradores llamados igual («Plantilla
  // nueva») no hay forma de distinguirlos en la biblioteca. El activo que se
  // elige acá es apenas el primero de la relación "Activos" — la misma que
  // luego se ve y se completa en la pestaña Configuración del editor.
  const crear = useMutation({
    mutationFn: () =>
      api.post<Plantilla>('/procedimientos', {
        nombre: nombreNuevo.trim(),
        puntuacionActiva: false,
        activos: activoNuevoId ? [activoNuevoId] : [],
        tipoTrabajo: tipoNuevo || undefined,
      }),
    onSuccess: async (plantilla) => {
      limpiarFormularioNuevo();
      await cliente.invalidateQueries({ queryKey: ['procedimientos'] });
      // Recién creada, entra directo a edición: está vacía, no hay nada que
      // «ver en detalle» todavía.
      navegar(`/procedimientos/${plantilla.id}`, { state: { modoEdicion: true } });
    },
  });

  const publicadas = data?.filter((p) => p.estado === 'PUBLICADA').length ?? 0;
  const sinPublicar = data?.filter((p) => p.tieneCambiosSinPublicar).length ?? 0;

  return (
    <>
      <div className="cabecera-pagina cabecera-lista">
        <div className="cabecera-lista-titulo">
          <h1>Procedimientos</h1>
          <EnlaceRuta to="/procedimientos/historial" className="boton secundario pequeno">
            Ver historial
          </EnlaceRuta>
        </div>
        <div className="campo cabecera-lista-buscar">
          <IconoLupa />
          <input
            aria-label="Buscar plantillas"
            placeholder="Nombre de la plantilla…"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {puede('procedimiento:escribir') ? (
            <>
              <BotonImportarCsv
                ruta="plantillas"
                alTerminar={() => void cliente.invalidateQueries({ queryKey: ['procedimientos'] })}
              />
              <button type="button" className="boton oculto-en-movil" onClick={() => setCreandoAbierto(true)}>
                Nueva plantilla
              </button>
              <button
                type="button"
                className="boton-flotante-crear"
                aria-label="Nueva plantilla"
                onClick={() => setCreandoAbierto(true)}
              >
                <IconoMas />
              </button>
            </>
          ) : null}
        </div>
      </div>

      {creandoAbierto ? (
        <Dialogo titulo="Nueva plantilla" alCerrar={limpiarFormularioNuevo}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (nombreNuevo.trim()) crear.mutate();
            }}
          >
            <div className="campo">
              <label htmlFor="nueva-plantilla-nombre">Título</label>
              <input
                id="nueva-plantilla-nombre"
                value={nombreNuevo}
                onChange={(e) => setNombreNuevo(e.target.value)}
                placeholder="Ej.: Inspección mensual de aire acondicionado"
                autoFocus
              />
            </div>
            <SelectorActivo id="nueva-plantilla-activo" valor={activoNuevoId} alCambiar={setActivoNuevoId} />
            {/* Sin Frecuencia acá a propósito: la recurrencia es de la OT, no
                de la plantilla — un procedimiento solo se usa adjunto a una
                OT, y es esa OT la que decide cada cuánto se repite. */}
            <SelectorLista
              id="nueva-plantilla-tipo"
              etiqueta="Tipo"
              valor={tipoNuevo}
              alCambiar={(v) => setTipoNuevo(v as TipoTrabajo | '')}
              opciones={TIPOS_TRABAJO.map((t) => ({ id: t, nombre: ETIQUETA_TIPO_TRABAJO[t] }))}
              textoVacio="Sin definir"
            />
            {crear.isError ? <ErroresFormulario error={crear.error} /> : null}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" className="boton secundario" onClick={limpiarFormularioNuevo}>
                Cancelar
              </button>
              <button type="submit" className="boton" disabled={!nombreNuevo.trim() || crear.isPending}>
                {crear.isPending ? 'Creando…' : 'Crear'}
              </button>
            </div>
          </form>
        </Dialogo>
      ) : null}

      <div className="rejilla-indicadores">
        <Indicador titulo="PLANTILLAS" valor={data?.length ?? '—'} tono="azul" />
        <Indicador titulo="PUBLICADAS" valor={publicadas} tono="verde" nota="Adjuntables a una orden" />
        <Indicador
          titulo="CON CAMBIOS SIN PUBLICAR"
          valor={sinPublicar}
          tono={sinPublicar > 0 ? 'ambar' : 'gris'}
          nota={sinPublicar > 0 ? 'No llegan a las órdenes hasta publicarse' : 'Todo al día'}
          className="col-ocultar-movil"
        />
      </div>

      <button
        type="button"
        className="boton secundario solo-movil boton-filtros-movil"
        onClick={() => setModalFiltrosAbierto(true)}
      >
        Filtros{(estado ? 1 : 0) + (grupoId.length > 0 ? 1 : 0) > 0 ? ` (${(estado ? 1 : 0) + (grupoId.length > 0 ? 1 : 0)})` : ''}
      </button>

      <div className="filtros filtros-escritorio">
        <SelectorLista
          id="procedimientos-filtro-estado"
          etiqueta="Estado"
          valor={estado}
          alCambiar={setEstado}
          opciones={ESTADOS_PLANTILLA.map((e) => ({ id: e, nombre: ETIQUETA_ESTADO_PLANTILLA[e] }))}
          textoVacio="Todos los estados"
        />
        <SelectorListaMultiple
          id="procedimientos-filtro-grupo"
          etiqueta="Grupo a cargo"
          seleccion={grupoId}
          alCambiar={setGrupoId}
          opciones={(grupos ?? []).map((g) => ({ id: g.id, nombre: g.nombre }))}
          textoVacio="Todos los grupos"
        />
      </div>

      {modalFiltrosAbierto ? (
        <Dialogo titulo="Filtros" alCerrar={() => setModalFiltrosAbierto(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <SelectorLista
              id="mf-procedimientos-estado"
              etiqueta="Estado"
              valor={estado}
              alCambiar={setEstado}
              opciones={ESTADOS_PLANTILLA.map((e) => ({ id: e, nombre: ETIQUETA_ESTADO_PLANTILLA[e] }))}
              textoVacio="Todos los estados"
            />
            <SelectorListaMultiple
              id="mf-procedimientos-grupo"
              etiqueta="Grupo a cargo"
              seleccion={grupoId}
              alCambiar={setGrupoId}
              opciones={(grupos ?? []).map((g) => ({ id: g.id, nombre: g.nombre }))}
              textoVacio="Todos los grupos"
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="boton" onClick={() => setModalFiltrosAbierto(false)}>
                Listo
              </button>
            </div>
          </div>
        </Dialogo>
      ) : null}

      <div className="panel">
        {isLoading ? <Cargando /> : null}
        {data && data.length === 0 ? <Vacio titulo="Sin plantillas" detalle="Cree la primera o impórtelas." /> : null}

        {data && data.length > 0 ? (
          esMovil ? (
            <div className="tarjetas-ot">
              {data.map((p) => (
                <div
                  key={p.id}
                  className="tarjeta-ot"
                  role="button"
                  tabIndex={0}
                  onClick={() => navegar(`/procedimientos/${p.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navegar(`/procedimientos/${p.id}`);
                  }}
                >
                  <div className="tarjeta-ot-cuerpo">
                    <div className="tarjeta-ot-cabecera">
                      <span className="numero-registro">#{p.numero}</span>
                      <Etiqueta tono={TONO_ESTADO[p.estado]}>{ETIQUETA_ESTADO_PLANTILLA[p.estado]}</Etiqueta>
                    </div>
                    <div className="tarjeta-ot-titulo">{p.nombre}</div>
                    {p.categoria ? <div className="tarjeta-ot-nota">{p.categoria}</div> : null}
                    {p.tieneCambiosSinPublicar && p.estado !== 'ARCHIVADA' ? (
                      <div style={{ fontSize: 11, color: 'var(--alerta)', fontWeight: 700 }}>
                        Cambios sin publicar
                      </div>
                    ) : null}
                    <div className="tarjeta-ot-datos">
                      <span>{p.cantidadCampos} campo(s)</span>
                      <span>v{p.version}</span>
                      <span>{p.ejecuciones} ejecución(es)</span>
                      {p.publicadoEl ? <span>{formatearFechaHora(p.publicadoEl)}</span> : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="tabla-contenedor">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>N.º</th>
                    <th>Plantilla</th>
                    <th>Estado</th>
                    <th>Campos</th>
                    <th>Versión</th>
                    <th>Ejecuciones</th>
                    <th>Publicada</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((p) => (
                    <tr key={p.id}>
                      <td className="numero-registro">#{p.numero}</td>
                      <td>
                        <EnlaceRuta to={`/procedimientos/${p.id}`} style={{ fontWeight: 600 }}>
                          {p.nombre}
                        </EnlaceRuta>
                        {p.categoria ? (
                          <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>{p.categoria}</div>
                        ) : null}
                      </td>
                      <td>
                        <Etiqueta tono={TONO_ESTADO[p.estado]}>{ETIQUETA_ESTADO_PLANTILLA[p.estado]}</Etiqueta>
                        {p.tieneCambiosSinPublicar && p.estado !== 'ARCHIVADA' ? (
                          <div style={{ fontSize: 11, color: 'var(--alerta)', fontWeight: 700, marginTop: 3 }}>
                            Cambios sin publicar
                          </div>
                        ) : null}
                      </td>
                      <td className="numero-registro">{p.cantidadCampos}</td>
                      <td className="numero-registro">v{p.version}</td>
                      <td className="numero-registro">{p.ejecuciones}</td>
                      <td>{p.publicadoEl ? formatearFechaHora(p.publicadoEl) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </div>
    </>
  );
}

// ─── Editor de árbol (§8.2 a §8.7) ───────────────────────────────────────────

/** Recorre el árbol aplicando una transformación sobre el nodo de una ruta. */
function transformar(
  items: ItemArbol[],
  ruta: number[],
  accion: (lista: ItemArbol[], indice: number) => ItemArbol[],
): ItemArbol[] {
  const [indice, ...resto] = ruta;
  if (indice === undefined) return items;

  if (resto.length === 0) return accion(items, indice);

  return items.map((item, i) =>
    i === indice ? { ...item, hijos: transformar(item.hijos, resto, accion) } : item,
  );
}

function nodoEn(items: ItemArbol[], ruta: number[]): ItemArbol | undefined {
  return ruta.reduce<ItemArbol | undefined>(
    (actual, indice) => (actual ? actual.hijos[indice] : items[indice]),
    undefined,
  );
}

const ETIQUETA_CAMPO_ERROR: Record<string, string> = {
  nombre: 'el nombre',
  opciones: 'las opciones',
};

/**
 * Traduce `items.2.hijos.0.nombre` (la ruta con la que Zod marca el campo
 * inválido) a la misma `ruta: number[]` que ya usa la selección del árbol —
 * así el aviso de error puede señalar y abrir el ítem exacto en vez de dejar
 * al usuario a adivinar cuál de varios ítems sin nombre es el que falta.
 */
function detallesLegibles(
  detalles: unknown,
  items: ItemArbol[],
): { ruta: number[] | null; texto: string }[] {
  if (!Array.isArray(detalles)) return [];

  return detalles.map((detalle) => {
    const campo = typeof detalle?.campo === 'string' ? detalle.campo : '';
    const mensaje = typeof detalle?.mensaje === 'string' ? detalle.mensaje : 'Dato inválido.';
    const ruta = [...campo.matchAll(/(?:items|hijos)\.(\d+)/g)].map((m) => Number(m[1]!));
    const partes = campo.split('.');
    const propiedad = partes[partes.length - 1] ?? '';

    if (ruta.length === 0) return { ruta: null, texto: mensaje };

    const nodo = nodoEn(items, ruta);
    const nombreNodo = nodo?.nombre?.trim() || `ítem #${ruta[ruta.length - 1]! + 1} sin nombre`;
    const etiqueta = ETIQUETA_CAMPO_ERROR[propiedad] ?? propiedad;
    // Un error de condición no es un campo que falta: se dice qué está mal.
    if (propiedad === 'condicion' || propiedad === 'opciones') return { ruta, texto: `«${nombreNodo}»: ${mensaje}` };
    return { ruta, texto: `«${nombreNodo}»: falta ${etiqueta} (${mensaje.toLowerCase()}).` };
  });
}

/**
 * Ítems sin nombre en todo el árbol, con la misma forma de `detalles` que
 * devuelve el servidor (`items.4.hijos.3.hijos.0.nombre`). Se chequea antes
 * de guardar: si no, el PUT del metadato sale igual, el del árbol falla con
 * 400 y la plantilla queda a medio guardar.
 */
function camposSinNombre(items: ItemArbol[], prefijo = 'items'): { campo: string; mensaje: string }[] {
  return items.flatMap((item, indice) => {
    const ruta = `${prefijo}.${indice}`;
    const propio = item.nombre.trim() ? [] : [{ campo: `${ruta}.nombre`, mensaje: 'Requerido' }];
    return [...propio, ...camposSinNombre(item.hijos, `${ruta}.hijos`)];
  });
}

const nuevoItem = (tipoItem: ItemArbol['tipoItem'], plantillaAnidadaId?: string): ItemArbol => ({
  tipoItem,
  nombre:
    tipoItem === 'SECCION'
      ? 'Nueva sección'
      : tipoItem === 'ENCABEZADO'
        ? 'Nuevo encabezado'
        : tipoItem === 'PROCEDIMIENTO_ANIDADO'
          ? 'Procedimiento anidado'
          : '',
  tipoCampo: tipoItem === 'CAMPO' ? 'TEXTO' : undefined,
  plantillaAnidadaId,
  opciones: [],
  requerido: false,
  permiteAdjuntos: false,
  permiteNotas: false,
  evidenciaObligatoria: false,
  imagenesReferencia: [],
  hijos: [],
});

/** Copia profunda de un ítem para "Duplicar": sin `id` (el servidor asigna uno
 * nuevo al guardar) y sin las imágenes de referencia de la fila original —
 * duplicar un campo no debe hacer que dos filas compartan las mismas fotos. */
function duplicarNodo(item: ItemArbol): ItemArbol {
  return {
    ...item,
    id: undefined,
    imagenesReferencia: [],
    hijos: item.hijos.map(duplicarNodo),
  };
}

function contarCamposArbol(items: ItemArbol[]): number {
  return items.reduce((t, i) => t + (i.tipoItem === 'CAMPO' ? 1 : 0) + contarCamposArbol(i.hijos), 0);
}

/**
 * Sostener una sección y llevarla con el mouse hacia el borde de abajo (o de
 * arriba) tiene que ir revelando lo que sigue, igual que arrastrar una tarjeta
 * en cualquier tablero — si no, no se puede soltar más abajo de lo que ya
 * entraba en pantalla. Se dispara en cada `mousemove` del arrastre a mano,
 * mientras el cursor está dentro de una franja cerca del borde del panel de
 * contenido.
 */
function autoscrollAlBordeDuranteArrastre(clientY: number): void {
  const contenedor = document.querySelector('.contenido') as HTMLElement | null;
  if (!contenedor) return;
  const rect = contenedor.getBoundingClientRect();
  const margen = 80;
  const distanciaArriba = clientY - rect.top;
  const distanciaAbajo = rect.bottom - clientY;
  // `contenedor.scrollTop = ...` no tiene efecto en este panel, y
  // `behavior: 'auto'` hereda el `scroll-behavior: smooth` del CSS, cuya
  // animación tampoco llega a aplicarse de forma confiable acá — solo
  // `scrollBy` con `'instant'` mueve el scroll de verdad (confirmado a mano).
  if (distanciaArriba < margen) {
    contenedor.scrollBy({ top: -(margen - distanciaArriba) * 0.7, behavior: 'instant' });
  } else if (distanciaAbajo < margen) {
    contenedor.scrollBy({ top: (margen - distanciaAbajo) * 0.7, behavior: 'instant' });
  }
}

export function EditorProcedimiento(): JSX.Element {
  const { id = '' } = useParams();
  const location = useLocation();
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const { puede } = useSesion();
  const { mostrarError } = useToasts();
  // El borrador local (items/nombre/descripción) sobrevive a un refetch en
  // segundo plano —p. ej. al guardar la descripción no debe perderse una
  // edición del árbol todavía sin guardar—, pero debe reiniciarse al entrar a
  // OTRA plantilla: se rastrea de cuál id son estos datos para no arrastrar
  // el borrador de la anterior a la nueva.
  const [idBorrador, setIdBorrador] = useState<string | null>(null);
  const [items, setItems] = useState<ItemArbol[] | null>(null);
  const [seleccion, setSeleccion] = useState<number[] | null>(null);
  // Ítem que el último guardado señaló como inválido (ruta «4.3.0»): se marca
  // en rojo hasta el siguiente intento de guardado.
  const [resaltada, setResaltada] = useState<string | null>(null);
  const [sucio, setSucio] = useState(false);
  const [nombre, setNombre] = useState<string | null>(null);
  const [descripcion, setDescripcion] = useState<string | null>(null);
  const [pestana, setPestana] = useState<'campos' | 'configuracion' | 'historial'>('campos');
  // Solo dos caminos entran en edición: crear una plantilla nueva (llega con
  // `state.modoEdicion`, § flujo de creación) o pedirlo explícitamente con
  // «Editar» desde el detalle — nunca por tener el permiso de por sí.
  const [modoEdicion, setModoEdicion] = useState(
    () => (location.state as { modoEdicion?: boolean } | null)?.modoEdicion ?? false,
  );

  // Configuración (§8.2): etiquetas + agenda de la plantilla. Igual que
  // nombre/descripción, el borrador local sobrevive a un refetch en segundo
  // plano pero se reinicia al entrar a otra plantilla.
  const [tiposFallaSel, setTiposFallaSel] = useState<string[] | null>(null);
  const [tiposActivoSel, setTiposActivoSel] = useState<string[] | null>(null);
  const [activosSel, setActivosSel] = useState<string[] | null>(null);
  const [ubicacionesSel, setUbicacionesSel] = useState<string[] | null>(null);
  const [gruposSel, setGruposSel] = useState<string[] | null>(null);
  const [tipoTrabajoSel, setTipoTrabajoSel] = useState<TipoTrabajo | '' | null>(null);

  // Comportamiento estándar de «seleccionar algo»: un clic fuera de cualquier
  // cuadro lo deselecciona, así vuelve a colapsarse solo, sin necesidad de
  // elegir otro para que el anterior se cierre. `mousedown` (no `click`)
  // porque dispara antes que el clic que sí selecciona algo nuevo — si no,
  // esta limpieza corre después y deshace la nueva selección.
  useEffect(() => {
    if (seleccion === null) return;
    const alHacerClic = (e: MouseEvent): void => {
      const objetivo = e.target as HTMLElement;
      // El panel del combo de tipo de campo vive en un portal a `document.body`
      // (`PanelFlotante`), fuera del `.item-procedimiento`: sin este chequeo, el
      // mousedown sobre una opción colapsaba la tarjeta —y desmontaba el botón—
      // antes de que su click llegara a cambiar el tipo (§ bug reportado).
      // La rama condicional (§8.5: "+ Agregar otra condición", el conector y
      // sus campos anidados) vive fuera del `.item-procedimiento` del campo
      // disparador —es hermana, no hija, para poder anidar su propio
      // `NivelArbol` recursivo— así que necesita la misma excepción: si no,
      // el mousedown sobre el botón colapsa la tarjeta antes de que su click
      // llegue a agregar el campo condicional (§ bug reportado: el botón «no
      // hace nada» — en realidad cerraba la tarjeta antes de que el click
      // llegara a registrarse).
      if (
        !objetivo.closest('.item-procedimiento') &&
        !objetivo.closest('.combo-panel') &&
        !objetivo.closest('.rama-condicional')
      )
        setSeleccion(null);
    };
    document.addEventListener('mousedown', alHacerClic);
    return () => document.removeEventListener('mousedown', alHacerClic);
  }, [seleccion]);

  const { data: fallas } = useTiposFalla();
  const { data: tiposActivo } = useTiposActivo();
  const { data: grupos } = useGruposOpciones();
  const { data: ubicacionesTodas } = useUbicaciones();
  const { data: activosTodos } = useQuery({
    queryKey: ['activos', 'para-configuracion-plantilla'],
    queryFn: () => api.get<Pagina<{ id: string; nombre: string }>>('/activos?tamano=100'),
  });

  const { data: plantilla, isLoading, error } = useQuery({
    queryKey: ['procedimiento', id],
    queryFn: () =>
      api.get<Plantilla & { items: Parameters<typeof desdeLectura>[0] }>(`/procedimientos/${id}`),
  });

  // El borrador local se sincroniza con la respuesta acá, en un efecto atado
  // a los datos — NO dentro del propio `queryFn` (como estaba antes): ese
  // side-effect solo corría cuando react-query hacía un fetch de verdad, así
  // que si la plantilla ya estaba en caché (volver a una que ya se había
  // abierto) `queryFn` no se reejecutaba y el borrador se quedaba en `null`
  // para siempre — pantalla de "El procedimiento no existe" hasta un F5 que
  // arrancaba el estado de cero (§ bug reportado).
  useEffect(() => {
    if (!plantilla) return;
    const arbol = desdeLectura(plantilla.items);
    const esBorradorVigente = idBorrador === id;
    setItems((previos) => (esBorradorVigente ? previos ?? arbol : arbol));
    setNombre((previo) => (esBorradorVigente ? previo ?? plantilla.nombre : plantilla.nombre));
    setDescripcion((previo) => (esBorradorVigente ? previo ?? plantilla.descripcion ?? '' : plantilla.descripcion ?? ''));
    setTiposFallaSel((previo) => (esBorradorVigente ? previo ?? plantilla.tiposFalla : plantilla.tiposFalla));
    setTiposActivoSel((previo) => (esBorradorVigente ? previo ?? plantilla.tiposActivo : plantilla.tiposActivo));
    setActivosSel((previo) => (esBorradorVigente ? previo ?? plantilla.activos : plantilla.activos));
    setUbicacionesSel((previo) => (esBorradorVigente ? previo ?? plantilla.ubicaciones : plantilla.ubicaciones));
    setGruposSel((previo) => (esBorradorVigente ? previo ?? plantilla.grupos : plantilla.grupos));
    setTipoTrabajoSel((previo) => (esBorradorVigente ? previo ?? (plantilla.tipoTrabajo ?? '') : plantilla.tipoTrabajo ?? ''));
    setIdBorrador(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantilla, id]);

  // Se recalcula en cada render: el aviso y el botón «Guardar borrador» reflejan
  // al instante qué falta, sin esperar a que el servidor rechace el guardado.
  const pendientesNombre = camposSinNombre(items ?? []);

  const guardar = useMutation({
    // Un solo botón —«Guardar borrador»— para todo lo pendiente: nombre,
    // descripción, árbol de campos y configuración (etiquetas, grupos, tipo),
    // sin importar en qué pestaña se esté parado. Antes «Configuración» tenía
    // su propio botón de guardado con los mismos datos pero sin pasar por el
    // árbol de campos, lo que daba una cabecera distinta por pestaña y, peor,
    // pisaba estos campos con los valores viejos si se guardaba desde
    // «Campos del Procedimiento» (§ bug reportado: cabeceras inconsistentes).
    mutationFn: async () => {
      // Red de seguridad: el botón ya está deshabilitado con ítems sin nombre,
      // pero se corta acá también para no mandar un guardado a medias.
      if (pendientesNombre.length > 0) {
        throw new ErrorApi('VALIDACION', 'Hay ítems sin nombre; complételos antes de guardar.', 400, pendientesNombre);
      }
      const limpio = (nombre ?? plantilla!.nombre).trim() || plantilla!.nombre;
      await api.put<Plantilla>(`/procedimientos/${id}`, {
        nombre: limpio,
        descripcion: descripcion || undefined,
        categoriaId: plantilla?.categoriaId ?? undefined,
        puntuacionActiva: plantilla?.puntuacionActiva ?? false,
        tipoTrabajo: tipoTrabajoSel || undefined,
        tiposFalla: tiposFallaSel ?? [],
        tiposActivo: tiposActivoSel ?? [],
        activos: activosSel ?? [],
        ubicaciones: ubicacionesSel ?? [],
        grupos: gruposSel ?? [],
      });
      return api.put<{ cantidadCampos: number }>(`/procedimientos/${id}/arbol`, { items: aEntrada(items ?? []) });
    },
    onMutate: () => setResaltada(null),
    onError: (error) => {
      // El primer ítem rechazado queda marcado y abierto, para que el error se
      // vea en el cuadro al que corresponde y no solo en el aviso de arriba.
      const primero = detallesLegibles((error as ErrorApi).detalles, items ?? [])[0];
      if (primero?.ruta) {
        setResaltada(primero.ruta.join('.'));
        setSeleccion(primero.ruta);
      }
    },
    onSuccess: async () => {
      setSucio(false);
      await cliente.invalidateQueries({ queryKey: ['procedimiento', id] });
    },
  });

  const publicar = useMutation({
    mutationFn: () => api.post<Plantilla>(`/procedimientos/${id}/publicar`),
    onMutate: () => setResaltada(null),
    onError: (error) => {
      // Misma regla que el guardado: el primer ítem rechazado se marca y se abre.
      const primero = detallesLegibles((error as ErrorApi).detalles, items ?? [])[0];
      if (primero?.ruta) {
        setResaltada(primero.ruta.join('.'));
        setSeleccion(primero.ruta);
      }
    },
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['procedimiento', id] });
      await cliente.invalidateQueries({ queryKey: ['procedimientos'] });
    },
  });

  // Menú «⋮» (§ pedido): copiar plantilla o eliminarla, cada una con su propia
  // confirmación aparte — un solo clic no alcanza para ninguna de las dos.
  // Copiar solo lleva el árbol de campos: título y descripción los define
  // quien copia, no se heredan de la original.
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [confirmandoEliminar, setConfirmandoEliminar] = useState(false);
  const [copiando, setCopiando] = useState(false);
  const [nombreCopia, setNombreCopia] = useState('');
  const [descripcionCopia, setDescripcionCopia] = useState('');

  const duplicarPlantilla = useMutation({
    mutationFn: () =>
      api.post<Plantilla>(`/procedimientos/${id}/duplicar`, {
        nombre: nombreCopia.trim(),
        descripcion: descripcionCopia.trim() || undefined,
      }),
    onSuccess: async (copia) => {
      setCopiando(false);
      await cliente.invalidateQueries({ queryKey: ['procedimientos'] });
      navegar(`/procedimientos/${copia.id}`, { state: { modoEdicion: true } });
    },
  });

  const eliminarPlantilla = useMutation({
    mutationFn: () => api.delete(`/procedimientos/${id}`),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['procedimientos'] });
      // `replace`: el editor de la plantilla eliminada no debe quedar en el historial.
      navegar('/procedimientos', { replace: true });
    },
  });

  if (isLoading) return <Cargando />;
  if (error) return <Aviso tono="error">{(error as ErrorApi).message}</Aviso>;
  if (!plantilla || items === null) return <Vacio titulo="El procedimiento no existe" />;

  const puedeEscribir = puede('procedimiento:escribir');
  const editable = puedeEscribir && modoEdicion;
  const campos = contarCamposArbol(items);

  const actualizar = (siguiente: ItemArbol[]): void => {
    setItems(siguiente);
    setSucio(true);
  };

  const agregarRaiz = (tipo: ItemArbol['tipoItem'], plantillaAnidadaId?: string): void => {
    actualizar([...items, nuevoItem(tipo, plantillaAnidadaId)]);
    // El campo recién creado abre expandido: es el que se va a nombrar de
    // una, no tiene sentido obligar a un segundo clic para verlo.
    setSeleccion([items.length]);
  };

  const agregarHijo = (ruta: number[], tipo: ItemArbol['tipoItem'], plantillaAnidadaId?: string): void => {
    const indiceNuevo = nodoEn(items, ruta)?.hijos.length ?? 0;
    actualizar(
      transformar(items, ruta, (lista, indice) =>
        lista.map((item, i) =>
          i === indice ? { ...item, hijos: [...item.hijos, nuevoItem(tipo, plantillaAnidadaId)] } : item,
        ),
      ),
    );
    setSeleccion([...ruta, indiceNuevo]);
  };

  const duplicar = (ruta: number[]): void => {
    actualizar(
      transformar(items, ruta, (lista, indice) => {
        const copia = [...lista];
        copia.splice(indice + 1, 0, duplicarNodo(lista[indice]!));
        return copia;
      }),
    );
  };

  const mover = (ruta: number[], delta: number): void => {
    actualizar(
      transformar(items, ruta, (lista, indice) => {
        const destino = indice + delta;
        if (destino < 0 || destino >= lista.length) return lista;
        const copia = [...lista];
        [copia[indice], copia[destino]] = [copia[destino]!, copia[indice]!];
        return copia;
      }),
    );
    setSeleccion(null);
  };

  // Arrastrar una sección directo a su nuevo lugar (§ pedido) — igual gesto
  // que reordenar opciones, en vez de solo poder subirla/bajarla de a un puesto.
  const moverA = (ruta: number[], destinoIndice: number): void => {
    actualizar(
      transformar(items, ruta, (lista, indice) => {
        if (destinoIndice < 0 || destinoIndice >= lista.length || destinoIndice === indice) return lista;
        const copia = [...lista];
        const [movido] = copia.splice(indice, 1);
        copia.splice(destinoIndice, 0, movido!);
        return copia;
      }),
    );
    setSeleccion(null);
  };

  const eliminar = (ruta: number[]): void => {
    actualizar(transformar(items, ruta, (lista, indice) => lista.filter((_, i) => i !== indice)));
    setSeleccion(null);
  };

  const editarNodo = (ruta: number[], cambios: Partial<ItemArbol>): void => {
    actualizar(
      transformar(items, ruta, (lista, indice) =>
        lista.map((item, i) => {
          if (i !== indice) return item;
          const siguiente: ItemArbol = { ...item, ...cambios };
          // Si cambia el tipo o las opciones del disparador, sus hijos condicionales
          // se ajustan a lo que el disparador ofrece ahora.
          if ('tipoCampo' in cambios || 'opciones' in cambios) {
            siguiente.hijos = item.hijos.map((hijo) => ({ ...hijo, condicion: ajustarCondicion(hijo.condicion, siguiente) }));
          }
          return siguiente;
        }),
      ),
    );
  };

  return (
    <>
      <div className="editor-procedimiento-cabecera">
        <div className="editor-procedimiento-cabecera-fila">
          <BotonVolver a="/procedimientos" etiqueta="Volver a Procedimientos" />

          <div className="tabs-subrayadas" role="tablist" aria-label="Secciones del procedimiento">
            <button type="button" role="tab" aria-selected={pestana === 'campos'} className={pestana === 'campos' ? 'activo' : ''} onClick={() => setPestana('campos')}>
              Campos del Procedimiento
            </button>
            <button type="button" role="tab" aria-selected={pestana === 'configuracion'} className={pestana === 'configuracion' ? 'activo' : ''} onClick={() => setPestana('configuracion')}>
              Configuración
            </button>
            <button type="button" role="tab" aria-selected={pestana === 'historial'} className={pestana === 'historial' ? 'activo' : ''} onClick={() => setPestana('historial')}>
              Historial
            </button>
          </div>

          <div className="editor-procedimiento-acciones">
            {/* El campo `estado` solo distingue «nunca publicada» de
                «publicada al menos una vez»: no se pone en BORRADOR solo
                porque haya una edición guardada después de publicar. La señal
                correcta de que hay cambios pendientes de publicar es
                `tieneCambiosSinPublicar` (mismo campo que ya usa la lista) —
                sin esto, el badge seguía en verde «Publicada» con cambios
                reales sin salir, y «Publicar» quedaba habilitado sin nada
                nuevo que publicar (§ bug reportado: flujo roto). */}
            {plantilla.tieneCambiosSinPublicar && plantilla.estado !== 'ARCHIVADA' ? (
              <Etiqueta tono="ambar">Cambios sin publicar</Etiqueta>
            ) : (
              <Etiqueta tono={TONO_ESTADO[plantilla.estado]}>{ETIQUETA_ESTADO_PLANTILLA[plantilla.estado]}</Etiqueta>
            )}
            {/* Mismos tres botones sin importar la pestaña activa — antes cada
                una mostraba una cabecera distinta (3 variantes) para lo que
                es, en el fondo, una sola acción de guardado (§ bug
                reportado: "3 headers distinto por cada tab"). */}
            {modoEdicion ? (
              <>
                <button
                  type="button"
                  className="boton secundario"
                  disabled={!sucio || guardar.isPending || pendientesNombre.length > 0}
                  title={pendientesNombre.length > 0 ? 'Hay ítems sin nombre: complételos para guardar' : undefined}
                  onClick={() => guardar.mutate()}
                >
                  Guardar borrador
                </button>
                {pendientesNombre.length > 0 ? (
                  <span style={{ color: 'var(--falla)', fontSize: 13, fontWeight: 600 }}>
                    Falta el nombre en {pendientesNombre.length} {pendientesNombre.length === 1 ? 'ítem' : 'ítems'}
                  </span>
                ) : null}
                <button
                  type="button"
                  className="boton"
                  disabled={sucio || !plantilla.tieneCambiosSinPublicar || publicar.isPending}
                  onClick={() => publicar.mutate()}
                >
                  Publicar
                </button>
                {!sucio ? (
                  <button type="button" className="boton secundario" onClick={() => setModoEdicion(false)}>
                    Listo
                  </button>
                ) : null}
              </>
            ) : puedeEscribir ? (
              <>
                {/* Publicar no debería exigir entrar a «Editar»: si ya hay un
                    borrador guardado con cambios pendientes, publicarlo es
                    una acción propia, no una edición (§ bug reportado: sin
                    esto, la única forma de publicar era abrir edición primero
                    aunque no hubiera nada que tocar). */}
                {plantilla.tieneCambiosSinPublicar && plantilla.estado !== 'ARCHIVADA' ? (
                  <button type="button" className="boton" disabled={publicar.isPending} onClick={() => publicar.mutate()}>
                    {publicar.isPending ? 'Publicando…' : 'Publicar'}
                  </button>
                ) : null}
                <button type="button" className="boton secundario" onClick={() => setModoEdicion(true)}>
                  Editar
                </button>
              </>
            ) : null}
            {puedeEscribir ? (
              <MenuOpcionesPlantilla
                alPedirCopiar={() => {
                  setNombreCopia(`${plantilla.nombre} (copia)`);
                  setDescripcionCopia('');
                  setCopiando(true);
                }}
                alPedirEliminar={() => setConfirmandoEliminar(true)}
                abierto={menuAbierto}
                alCambiarAbierto={setMenuAbierto}
              />
            ) : null}
          </div>
        </div>
      </div>

      {copiando ? (
        <Dialogo titulo="Copiar plantilla" alCerrar={() => setCopiando(false)}>
          <p className="campo-nota" style={{ marginTop: 0 }}>
            Se copian los campos de «{plantilla.nombre}»; título, descripción y el resto de la configuración
            arrancan en blanco.
          </p>
          <div className="campo">
            <label htmlFor="copia-nombre">Título</label>
            <input id="copia-nombre" value={nombreCopia} onChange={(e) => setNombreCopia(e.target.value)} autoFocus />
          </div>
          <div className="campo">
            <label htmlFor="copia-descripcion">Descripción (opcional)</label>
            <textarea id="copia-descripcion" rows={2} value={descripcionCopia} onChange={(e) => setDescripcionCopia(e.target.value)} />
          </div>
          {duplicarPlantilla.isError ? <ErroresFormulario error={duplicarPlantilla.error} /> : null}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" className="boton secundario" onClick={() => setCopiando(false)}>
              Cancelar
            </button>
            <button
              type="button"
              className="boton"
              disabled={!nombreCopia.trim() || duplicarPlantilla.isPending}
              onClick={() => duplicarPlantilla.mutate()}
            >
              {duplicarPlantilla.isPending ? 'Copiando…' : 'Copiar'}
            </button>
          </div>
        </Dialogo>
      ) : null}

      {confirmandoEliminar ? (
        <Dialogo titulo="Eliminar plantilla" alCerrar={() => setConfirmandoEliminar(false)}>
          <p>
            ¿Eliminar <strong>{plantilla.nombre}</strong>? Desaparece de la biblioteca; las ejecuciones que ya usaron
            esta plantilla en una orden no se ven afectadas.
          </p>
          {eliminarPlantilla.isError ? <ErroresFormulario error={eliminarPlantilla.error} /> : null}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" className="boton secundario" onClick={() => setConfirmandoEliminar(false)}>
              Cancelar
            </button>
            <button
              type="button"
              className="boton peligro"
              disabled={eliminarPlantilla.isPending}
              onClick={() => eliminarPlantilla.mutate()}
            >
              {eliminarPlantilla.isPending ? 'Eliminando…' : 'Eliminar'}
            </button>
          </div>
        </Dialogo>
      ) : null}

      <div className="lienzo-contenedor">
      {modoEdicion && pendientesNombre.length > 0 ? (
        <Aviso tono="error">
          <div>
            No se puede guardar: {pendientesNombre.length === 1 ? 'hay 1 ítem sin nombre' : `hay ${pendientesNombre.length} ítems sin nombre`}.
            Escriba un nombre o pregunta en cada uno (aparecen marcados en rojo).
          </div>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            {detallesLegibles(pendientesNombre, items).map((d, i) =>
              d.ruta ? (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => setSeleccion(d.ruta)}
                    style={{
                      padding: 0,
                      border: 'none',
                      background: 'none',
                      color: 'inherit',
                      font: 'inherit',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    {d.texto}
                  </button>
                </li>
              ) : (
                <li key={i}>{d.texto}</li>
              ),
            )}
          </ul>
        </Aviso>
      ) : null}
      {guardar.isError ? (
        <Aviso tono="error">
          <div>{(guardar.error as ErrorApi).message}</div>
          {detallesLegibles((guardar.error as ErrorApi).detalles, items).length > 0 ? (
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {detallesLegibles((guardar.error as ErrorApi).detalles, items).map((d, i) =>
                d.ruta ? (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => setSeleccion(d.ruta)}
                      style={{
                        padding: 0,
                        border: 'none',
                        background: 'none',
                        color: 'inherit',
                        font: 'inherit',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      {d.texto}
                    </button>
                  </li>
                ) : (
                  <li key={i}>{d.texto}</li>
                ),
              )}
            </ul>
          ) : null}
        </Aviso>
      ) : null}
      {publicar.isError ? <ErroresFormulario error={publicar.error} /> : null}

      <div className="encabezado-lienzo">
        <input
          id="plantilla-nombre"
          aria-label="Nombre del procedimiento"
          className="campo-titulo-lienzo"
          value={nombre ?? ''}
          disabled={!editable}
          onChange={(e) => {
            setNombre(e.target.value);
            setSucio(true);
          }}
          onBlur={() => {
            // No se guarda solo; se restaura si queda vacío para que el
            // «Guardar borrador» no reciba un nombre inválido.
            if (!(nombre ?? '').trim()) {
              setNombre(plantilla.nombre);
              mostrarError('El nombre no puede quedar vacío; se restauró el anterior.');
            }
          }}
        />
        <textarea
          id="plantilla-descripcion"
          aria-label="Descripción del procedimiento"
          className="campo-descripcion-lienzo"
          placeholder="Agregue una descripción…"
          rows={2}
          value={descripcion ?? ''}
          disabled={!editable}
          onChange={(e) => {
            setDescripcion(e.target.value);
            setSucio(true);
          }}
        />
      </div>

      {pestana === 'campos' ? (
        <>
          {/* §8.8: lo que se edita no llega a las órdenes hasta publicarse. */}
          {sucio ? (
            <Aviso tono="atencion">
              Hay cambios sin guardar. Las órdenes de trabajo siguen usando la versión publicada.
            </Aviso>
          ) : plantilla.tieneCambiosSinPublicar && plantilla.estado !== 'ARCHIVADA' ? (
            <Aviso tono="atencion">
              El borrador tiene cambios que aún no se publicaron: las órdenes nuevas seguirán adjuntando la
              versión {plantilla.version}.
            </Aviso>
          ) : null}

          {campos > MAX_CAMPOS_PLANTILLA * 0.9 ? (
            <Aviso tono={campos > MAX_CAMPOS_PLANTILLA ? 'error' : 'atencion'}>
              {campos} campos de un máximo de {MAX_CAMPOS_PLANTILLA}. El conteo incluye los procedimientos
              anidados ya resueltos.
            </Aviso>
          ) : null}

          <div className="lienzo-con-barra-lateral">
            <IndiceSecciones
              secciones={items.flatMap((it, indice) =>
                it.tipoItem === 'SECCION' ? [{ id: `seccion-${indice}`, nombre: it.nombre }] : [],
              )}
            />
            <section className="panel">
              <div className="panel-cabecera">
                <h2>Campos del procedimiento</h2>
                <span className="nav-contador">
                  {campos} / {MAX_CAMPOS_PLANTILLA}
                </span>
              </div>
              <div className="panel-cuerpo">
                {items.length === 0 ? (
                  <Vacio
                    titulo="La plantilla está vacía"
                    detalle="Selecciona nuevos elementos de la barra lateral para empezar a añadirlos."
                  />
                ) : (
                  <NivelArbol
                    items={items}
                    ruta={[]}
                    seleccion={seleccion} resaltada={resaltada}
                    editable={editable}
                    puntuacionActiva={plantilla.puntuacionActiva}
                    onMover={mover}
                    onMoverA={moverA}
                    onEliminar={eliminar}
                    onAgregarHijo={agregarHijo}
                    onDuplicar={duplicar}
                    onEditar={editarNodo}
                    onSeleccionar={setSeleccion}
                  />
                )}
              </div>
            </section>
            {editable ? <BarraAgregarItem onAgregar={(tipo, plantillaId) => agregarRaiz(tipo, plantillaId)} /> : null}
          </div>
        </>
      ) : pestana === 'historial' ? (
        <HistorialDePlantilla plantillaId={id} />
      ) : (
        <>
          {guardar.isError ? <ErroresFormulario error={guardar.error} /> : null}

          <section className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-cabecera">
              <h2>Etiqueta tu procedimiento</h2>
            </div>
            <div className="panel-cuerpo">
              <p style={{ marginTop: 0, color: 'var(--texto-medio)', fontSize: 13 }}>
                Añade etiquetas a este procedimiento para que puedas encontrarlo fácilmente en tu biblioteca.
              </p>
              <SeleccionMultiple
                etiqueta="Tipos de activo"
                opciones={(tiposActivo ?? []).map((t) => ({ id: t.id, nombre: t.nombre }))}
                seleccion={tiposActivoSel ?? []}
                alCambiar={(v) => {
                  setTiposActivoSel(v);
                  setSucio(true);
                }}
                vacio="Aún no hay tipos de activo."
                soloLectura={!editable}
              />
              <SeleccionMultiple
                etiqueta="Tipos de Falla"
                opciones={(fallas ?? []).map((f) => ({ id: f.id, nombre: f.nombre }))}
                seleccion={tiposFallaSel ?? []}
                alCambiar={(v) => {
                  setTiposFallaSel(v);
                  setSucio(true);
                }}
                vacio="Aún no hay tipos de falla."
                soloLectura={!editable}
              />
              <SeleccionMultiple
                etiqueta="Activos"
                opciones={(activosTodos?.datos ?? []).map((a) => ({ id: a.id, nombre: a.nombre }))}
                seleccion={activosSel ?? []}
                alCambiar={(v) => {
                  setActivosSel(v);
                  setSucio(true);
                }}
                vacio="Sin activos."
                soloLectura={!editable}
              />
              <SeleccionMultiple
                etiqueta="Ubicaciones"
                opciones={(ubicacionesTodas ?? []).map((u) => ({ id: u.id, nombre: u.nombre }))}
                seleccion={ubicacionesSel ?? []}
                alCambiar={(v) => {
                  setUbicacionesSel(v);
                  setSucio(true);
                }}
                vacio="Sin ubicaciones."
                soloLectura={!editable}
              />
            </div>
          </section>

          <section className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-cabecera">
              <h2>Grupos a cargo</h2>
            </div>
            <div className="panel-cuerpo">
              <p style={{ marginTop: 0, color: 'var(--texto-medio)', fontSize: 13 }}>
                Gestiona quién es el responsable de este procedimiento. Un usuario no administrador
                solo puede elegirlo al crear una OT si pertenece a alguno de los grupos marcados
                acá; sin ningún grupo a cargo, nadie (salvo Administrador) podrá verlo.
              </p>
              <SeleccionMultiple
                etiqueta="Grupos"
                opciones={(grupos ?? []).map((g) => ({ id: g.id, nombre: g.nombre }))}
                seleccion={gruposSel ?? []}
                alCambiar={(v) => {
                  setGruposSel(v);
                  setSucio(true);
                }}
                vacio="Aún no hay grupos."
                soloLectura={!editable}
              />
            </div>
          </section>

          <section className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-cabecera">
              <h2>Tipo</h2>
            </div>
            <div className="panel-cuerpo">
              {/* Sin Frecuencia acá a propósito: la recurrencia es de la OT,
                  no de la plantilla — ver la misma nota en el diálogo de
                  creación, más arriba en este archivo. */}
              <SelectorLista
                id="plantilla-tipo"
                etiqueta="Tipo"
                valor={tipoTrabajoSel ?? ''}
                alCambiar={(v) => {
                  setTipoTrabajoSel(v as TipoTrabajo | '');
                  setSucio(true);
                }}
                opciones={TIPOS_TRABAJO.map((t) => ({ id: t, nombre: ETIQUETA_TIPO_TRABAJO[t] }))}
                textoVacio="Sin definir"
                deshabilitado={!editable}
              />
            </div>
          </section>
        </>
      )}
      </div>
    </>
  );
}

/**
 * Pestaña «Historial» del editor (§ pedido): en cuántas órdenes de trabajo
 * estuvo esta plantilla, sin salir a buscarla en el historial general de
 * ejecuciones — que es el mismo dato, solo que ahí sin filtrar por plantilla.
 */
function HistorialDePlantilla({ plantillaId }: { plantillaId: string }): JSX.Element {
  const [pagina, setPagina] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['ejecuciones', 'de-plantilla', plantillaId, pagina],
    queryFn: () =>
      api.get<Pagina<EjecucionResumida>>(`/ejecuciones${consulta({ pagina, tamano: 25, plantillaId })}`),
  });

  return (
    <section className="panel">
      <div className="panel-cabecera">
        <h2>Órdenes de trabajo donde se usó</h2>
        <span className="nav-contador">{data?.total ?? 0}</span>
      </div>
      <div className="panel-cuerpo">
        {isLoading ? <Cargando /> : null}
        {!isLoading && (data?.datos.length ?? 0) === 0 ? (
          <Vacio titulo="Sin ejecuciones" detalle="Esta plantilla todavía no se adjuntó a ninguna orden de trabajo." />
        ) : null}
        {(data?.datos.length ?? 0) > 0 ? (
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Orden de trabajo</th>
                  <th>Estado</th>
                  <th>Ubicación</th>
                  <th>Activo</th>
                  <th>Iniciada</th>
                  <th>Completada</th>
                </tr>
              </thead>
              <tbody>
                {data!.datos.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <EnlaceRuta to={`/ordenes/${e.ordenTrabajoId}`} style={{ fontWeight: 600 }}>
                        OT #{e.ordenTrabajoNumero}
                      </EnlaceRuta>
                    </td>
                    <td>
                      <Etiqueta tono={TONO_RESULTADO[e.estado]}>
                        {e.estado === 'COMPLETADA' ? 'Completada' : 'En progreso'}
                      </Etiqueta>
                    </td>
                    <td>{e.ubicacion ?? '—'}</td>
                    <td>{e.activo ?? '—'}</td>
                    <td>{formatearFechaHora(e.iniciadoEl)}</td>
                    <td>{e.completadoEl ? formatearFechaHora(e.completadoEl) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <Paginacion pagina={pagina} tamano={25} total={data?.total ?? 0} onCambiar={setPagina} />
      </div>
    </section>
  );
}

/** Valores que puede tomar el padre, para construir la condición de un hijo. */
/**
 * Ajusta la condición de un hijo cuando cambia su disparador (tipo u opciones).
 * Conserva solo los valores que el disparador todavía ofrece; si no queda
 * ninguno, o el operador ya no aplica al nuevo tipo, la condición se quita.
 * Sin esto, un valor viejo («Satisfecho») sigue guardado sin poder desmarcarse.
 */
function ajustarCondicion(condicion: Condicion | undefined, padre: ItemArbol): Condicion | undefined {
  if (!condicion) return undefined;
  const tipo = padre.tipoCampo ?? 'TEXTO';
  if (!(operadoresCondicionPara(tipo) as readonly string[]).includes(condicion.operador)) return undefined;
  const discretos = valoresDiscretosCondicion(tipo, padre.opciones);
  if (discretos === null) return condicion;
  // Canonizar y no comparar tal cual: lo guardado puede venir en mayúsculas
  // («MANTENIMIENTO») de una importación, y la opción se muestra como «Mantenimiento».
  // Una comparación exacta descartaba esos valores al abrir el editor, y el
  // siguiente guardado borraba la condición sin que nadie lo notara.
  const valores = condicion.valores
    .map((v) => canonicoDiscreto(discretos, v))
    .filter((v): v is string => v !== undefined);
  return valores.length > 0 ? { operador: condicion.operador, valores: [...new Set(valores)] } : undefined;
}

function valoresPosibles(tipoCampo: TipoCampoProcedimiento | undefined, opciones: string[]): string[] {
  if (tipoCampo === 'INSPECCION') return [...RESULTADOS_INSPECCION];
  if (tipoCampo === 'SI_NO_NA') return [...RESPUESTAS_TERNARIAS];
  if (tipoCampo === 'ELECCION_MULTIPLE') return opciones;
  return [];
}

/**
 * Barra "Selecciona nuevos elementos…" (§8.2): los mismos 4 tipos de ítem se
 * pueden agregar en la raíz del procedimiento o dentro de cualquier sección.
 *
 * Dos variantes de layout, no solo de estilo: en la raíz («lateral») queda
 * fija al costado del lienzo, siempre a mano mientras se compone toda la
 * plantilla. Dentro de una sección («inline») compite por el mismo ancho que
 * sus hijos — flotarla ahí (mismo look que la lateral) la saca de contexto y
 * se ve como un elemento roto, no como una barra de herramientas.
 */
function BarraAgregarItem({
  onAgregar,
  variante = 'lateral',
}: {
  onAgregar: (tipo: ItemArbol['tipoItem'], plantillaAnidadaId?: string) => void;
  variante?: 'lateral' | 'inline';
}): JSX.Element {
  const [eligiendoAnidado, setEligiendoAnidado] = useState(false);

  if (variante === 'inline') {
    return (
      <>
        <div className="barra-agregar-inline">
          <button type="button" className="boton secundario pequeno" onClick={() => onAgregar('CAMPO')}>
            + Campo
          </button>
          <button type="button" className="boton secundario pequeno" onClick={() => onAgregar('ENCABEZADO')}>
            + Encabezado
          </button>
          <button type="button" className="boton secundario pequeno" onClick={() => onAgregar('SECCION')}>
            + Sección
          </button>
          <button type="button" className="boton secundario pequeno" onClick={() => setEligiendoAnidado(true)}>
            + Procedimiento
          </button>
        </div>
        {eligiendoAnidado ? (
          <SelectorProcedimientoAnidado
            alCerrar={() => setEligiendoAnidado(false)}
            alElegir={(plantillaId) => {
              onAgregar('PROCEDIMIENTO_ANIDADO', plantillaId);
              setEligiendoAnidado(false);
            }}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      <div className="barra-lateral-agregar">
        <span className="barra-lateral-agregar-titulo">Artículo nuevo</span>
        <button type="button" onClick={() => onAgregar('CAMPO')}>
          <IconoMas />
          Campo
        </button>
        <button type="button" onClick={() => onAgregar('ENCABEZADO')}>
          <span aria-hidden="true" style={{ fontWeight: 700, fontSize: 13 }}>Tt</span>
          Encabezado
        </button>
        <button type="button" onClick={() => onAgregar('SECCION')}>
          <span aria-hidden="true" style={{ fontSize: 15 }}>▤</span>
          Sección
        </button>
        <button type="button" onClick={() => setEligiendoAnidado(true)}>
          <IconoArchivo />
          Procedimiento
        </button>
      </div>
      {eligiendoAnidado ? (
        <SelectorProcedimientoAnidado
          alCerrar={() => setEligiendoAnidado(false)}
          alElegir={(plantillaId) => {
            onAgregar('PROCEDIMIENTO_ANIDADO', plantillaId);
            setEligiendoAnidado(false);
          }}
        />
      ) : null}
    </>
  );
}

function SelectorProcedimientoAnidado({
  alCerrar,
  alElegir,
}: {
  alCerrar: () => void;
  alElegir: (plantillaId: string) => void;
}): JSX.Element {
  const { data } = useQuery({
    queryKey: ['procedimientos', 'PUBLICADA', ''],
    queryFn: () => api.get<Plantilla[]>(`/procedimientos${consulta({ estado: 'PUBLICADA' })}`),
  });
  const [plantillaId, setPlantillaId] = useState('');
  return (
    <Dialogo titulo="Agregar procedimiento anidado" alCerrar={alCerrar}>
      <SelectorLista
        id="anidado-plantilla"
        etiqueta="Plantilla publicada"
        valor={plantillaId}
        alCambiar={setPlantillaId}
        opciones={(data ?? []).map((p) => ({ id: p.id, nombre: p.nombre }))}
        textoVacio="Elija una plantilla…"
        vacio="No hay plantillas publicadas todavía."
      />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" className="boton secundario" onClick={alCerrar}>
          Cancelar
        </button>
        <button type="button" className="boton" disabled={!plantillaId} onClick={() => alElegir(plantillaId)}>
          Agregar
        </button>
      </div>
    </Dialogo>
  );
}

interface PropsItemComun {
  item: ItemArbol;
  activo: boolean;
  editable: boolean;
  primero: boolean;
  ultimo: boolean;
  onSubir: () => void;
  onBajar: () => void;
  onEliminar: () => void;
  onDuplicar: () => void;
  onEditar: (cambios: Partial<ItemArbol>) => void;
  onSeleccionar: () => void;
  /** Valores de respuesta del padre directo, para ofrecer la condición de este ítem (§8.5). */
  valoresPadre?: string[] | null;
  /** Tipo de campo del padre directo — decide la etiqueta y los operadores disponibles (§8.5). */
  tipoCampoPadre?: TipoCampoProcedimiento | null;
  /** El último guardado falló por este ítem: se marca en rojo para ubicarlo. */
  resaltado?: boolean;
}

function NivelArbol({
  items,
  ruta,
  seleccion,
  resaltada,
  editable,
  puntuacionActiva,
  valoresPadre = null,
  tipoCampoPadre = null,
  decoradorHijo,
  onMover,
  onMoverA,
  onEliminar,
  onAgregarHijo,
  onDuplicar,
  onEditar,
  onSeleccionar,
}: {
  items: ItemArbol[];
  ruta: number[];
  seleccion: number[] | null;
  /** Ruta («4.3.0») del ítem que el último guardado señaló como inválido. */
  resaltada: string | null;
  editable: boolean;
  puntuacionActiva: boolean;
  valoresPadre?: string[] | null;
  tipoCampoPadre?: TipoCampoProcedimiento | null;
  /** Contenido a insertar antes de cada ítem de este nivel — solo lo usa el
   * nivel de hijos condicionales (§8.5), para dibujar el bloque "Si respuesta
   * es…" conectado al disparador, fuera de la tarjeta del hijo. */
  decoradorHijo?: (item: ItemArbol, indice: number) => ReactNode;
  onMover: (ruta: number[], delta: number) => void;
  onMoverA: (ruta: number[], destinoIndice: number) => void;
  onEliminar: (ruta: number[]) => void;
  onAgregarHijo: (ruta: number[], tipo: ItemArbol['tipoItem'], plantillaAnidadaId?: string) => void;
  onDuplicar: (ruta: number[]) => void;
  onEditar: (ruta: number[], cambios: Partial<ItemArbol>) => void;
  onSeleccionar: (ruta: number[] | null) => void;
}): JSX.Element {
  // Arrastrar una sección a su nuevo puesto (§ pedido) — a mano con
  // mousedown/mousemove/mouseup en vez del drag & drop nativo del navegador:
  // el nativo (a) no deja ver la tarjeta moviéndose con el cursor, solo el
  // cursor genérico del sistema, y (b) en Chrome/Windows bloquea la rueda del
  // mouse durante todo el arrastre, algo que no se puede evitar desde JS. Con
  // mousemove ninguna de las dos limitaciones aplica. Solo se aplica entre
  // hermanas del mismo nivel, de ahí que el estado se guarde local a este
  // `NivelArbol`.
  const [arrastrando, setArrastrando] = useState<number | null>(null);
  const [objetivo, setObjetivo] = useState<number | null>(null);
  const [posicionCursor, setPosicionCursor] = useState<{ x: number; y: number } | null>(null);
  const objetivoRef = useRef<number | null>(null);
  const nivelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (arrastrando === null) return;

    const alMover = (e: MouseEvent): void => {
      setPosicionCursor({ x: e.clientX, y: e.clientY });
      autoscrollAlBordeDuranteArrastre(e.clientY);

      // Hit-testing a mano contra las hermanas de este nivel (no
      // `elementFromPoint`, que agarraría la tarjeta flotante de arriba si no
      // tuviera cuidado con `pointer-events: none`, y esto es más directo).
      const hermanas = nivelRef.current?.querySelectorAll<HTMLElement>(':scope > [data-seccion-indice]') ?? [];
      let encontrado: number | null = null;
      hermanas.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (e.clientY >= r.top && e.clientY <= r.bottom) encontrado = Number(el.dataset.seccionIndice);
      });
      objetivoRef.current = encontrado;
      setObjetivo(encontrado);
    };

    const alSoltar = (): void => {
      if (objetivoRef.current !== null) onMoverA(ruta.concat(arrastrando), objetivoRef.current);
      setArrastrando(null);
      setObjetivo(null);
      setPosicionCursor(null);
      objetivoRef.current = null;
    };

    window.addEventListener('mousemove', alMover);
    window.addEventListener('mouseup', alSoltar);
    return () => {
      window.removeEventListener('mousemove', alMover);
      window.removeEventListener('mouseup', alSoltar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrastrando]);

  const nombreSeccionArrastrada = arrastrando !== null ? (items[arrastrando]?.nombre || 'Nueva sección') : null;

  return (
    <div ref={nivelRef} style={{ display: 'flex', flexDirection: 'column', gap: 12, position: 'relative' }}>
      {arrastrando !== null && posicionCursor ? (
        <div
          className="seccion-arrastre-flotante"
          style={{ left: posicionCursor.x + 14, top: posicionCursor.y + 14 }}
        >
          <IconoArrastrar />
          {nombreSeccionArrastrada}
        </div>
      ) : null}
      {items.map((item, indice) => {
        const rutaItem = [...ruta, indice];
        // Fuera de modo edición (§ pedido): la vista normal solo muestra el
        // resumen colapsado de cada ítem, igual que la pestaña Configuración
        // — nada se expande ni reacciona al clic hasta entrar a "Editar".
        const comun: PropsItemComun = {
          item,
          activo: editable && seleccion?.join('.') === rutaItem.join('.'),
          editable,
          primero: indice === 0,
          ultimo: indice === items.length - 1,
          onSubir: () => onMover(rutaItem, -1),
          onBajar: () => onMover(rutaItem, 1),
          onEliminar: () => onEliminar(rutaItem),
          onDuplicar: () => onDuplicar(rutaItem),
          onEditar: (cambios) => onEditar(rutaItem, cambios),
          onSeleccionar: () => {
            if (editable) onSeleccionar(rutaItem);
          },
          valoresPadre,
          tipoCampoPadre,
          resaltado: resaltada === rutaItem.join('.'),
        };
        const decoracion = decoradorHijo ? decoradorHijo(item, indice) : null;

        if (item.tipoItem === 'ENCABEZADO') {
          return (
            <Fragment key={rutaItem.join('.')}>
              {decoracion}
              <TarjetaEncabezado {...comun} />
            </Fragment>
          );
        }

        if (item.tipoItem === 'SECCION') {
          return (
            <Fragment key={rutaItem.join('.')}>
              {decoracion}
              <div
                id={ruta.length === 0 ? `seccion-${indice}` : undefined}
                data-seccion-indice={indice}
                className={[
                  arrastrando === indice ? 'arbol-arrastrando' : '',
                  objetivo === indice && arrastrando !== null && arrastrando !== indice ? 'arbol-objetivo-arrastre' : '',
                ]
                  .filter(Boolean)
                  .join(' ') || undefined}
              >
                <TarjetaSeccion
                  {...comun}
                  arrastrable={editable}
                  onIniciarArrastre={() => setArrastrando(indice)}
                >
                  {/* Igual que el resto de los cuadros (§ pedido): en observación
                      (sin seleccionar) no se ve ningún control de edición, solo
                      el contenido. Agregar hijos es una acción de edición, así
                      que espera a que la sección esté activa. */}
                  {editable && comun.activo ? (
                    <div style={{ marginBottom: item.hijos.length > 0 ? 12 : 0 }}>
                      <BarraAgregarItem
                        variante="inline"
                        onAgregar={(tipo, plantillaId) => onAgregarHijo(rutaItem, tipo, plantillaId)}
                      />
                    </div>
                  ) : null}
                  {item.hijos.length > 0 ? (
                    <NivelArbol
                      items={item.hijos}
                      ruta={rutaItem}
                      seleccion={seleccion} resaltada={resaltada}
                      editable={editable}
                      puntuacionActiva={puntuacionActiva}
                      onMover={onMover}
                      onMoverA={onMoverA}
                      onEliminar={onEliminar}
                      onAgregarHijo={onAgregarHijo}
                      onDuplicar={onDuplicar}
                      onEditar={onEditar}
                      onSeleccionar={onSeleccionar}
                    />
                  ) : null}
                </TarjetaSeccion>
              </div>
            </Fragment>
          );
        }

        if (item.tipoItem === 'PROCEDIMIENTO_ANIDADO') {
          return (
            <Fragment key={rutaItem.join('.')}>
              {decoracion}
              <TarjetaProcedimientoAnidado {...comun} />
            </Fragment>
          );
        }

        // §8.5: un campo de respuesta acotada (inspección, sí/no/na, elección
        // múltiple o lista de comprobación) puede tener campos hijos que solo
        // se muestran cuando la respuesta cae en cierto valor — ej. pedir
        // observaciones solo si la inspección da FALLA.
        // Todo campo puede ser disparador (§8.5): los de respuesta discreta
        // ofrecen sus valores; el resto, valores que el autor escribe (`null`).
        const valoresDiscretos = valoresDiscretosCondicion(item.tipoCampo ?? 'TEXTO', item.opciones);
        return (
          <Fragment key={rutaItem.join('.')}>
            {decoracion}
            <div className={`campo-con-condicionales${item.hijos.length > 0 ? ' tiene-condicionales' : ''}`}>
              <TarjetaCampo {...comun} puntuacionActiva={puntuacionActiva} />
              <div className="rama-condicional">
                {item.hijos.length > 0 ? (
                  <NivelArbol
                    items={item.hijos}
                    ruta={rutaItem}
                    seleccion={seleccion} resaltada={resaltada}
                    editable={editable}
                    puntuacionActiva={puntuacionActiva}
                    decoradorHijo={(hijo, i) => (
                      <ConectorCondicional
                        condicion={hijo.condicion}
                        valoresPosibles={valoresDiscretos ?? []}
                        valoresLibres={valoresDiscretos === null}
                        tipoCampoPadre={item.tipoCampo ?? null}
                        editable={editable}
                        onCambiar={(condicion) => onEditar([...rutaItem, i], { condicion })}
                        onEliminarRama={() => onEliminar([...rutaItem, i])}
                      />
                    )}
                    onMover={onMover}
                    onMoverA={onMoverA}
                    onEliminar={onEliminar}
                    onAgregarHijo={onAgregarHijo}
                    onDuplicar={onDuplicar}
                    onEditar={onEditar}
                    onSeleccionar={onSeleccionar}
                  />
                ) : null}
                {editable && comun.activo ? (
                  <button
                    type="button"
                    className="boton-agregar-condicion"
                    onClick={() => onAgregarHijo(rutaItem, 'CAMPO')}
                  >
                    <IconoMas /> Agregar otra condición
                  </button>
                ) : null}
              </div>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}

/** Menú «⋮» del encabezado del procedimiento: copiar o eliminar (§ pedido). */
function MenuOpcionesPlantilla({
  abierto,
  alCambiarAbierto,
  alPedirCopiar,
  alPedirEliminar,
}: {
  abierto: boolean;
  alCambiarAbierto: (abierto: boolean) => void;
  alPedirCopiar: () => void;
  alPedirEliminar: () => void;
}): JSX.Element {
  const contenedorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const alHacerClic = (e: MouseEvent): void => {
      if (!contenedorRef.current?.contains(e.target as Node)) alCambiarAbierto(false);
    };
    document.addEventListener('mousedown', alHacerClic);
    return () => document.removeEventListener('mousedown', alHacerClic);
  }, [abierto, alCambiarAbierto]);

  return (
    <div ref={contenedorRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="boton-icono"
        aria-label="Más opciones"
        aria-haspopup="menu"
        aria-expanded={abierto}
        onClick={() => alCambiarAbierto(!abierto)}
      >
        <IconoOpciones />
      </button>
      {abierto ? (
        <div className="menu-opciones-plantilla" role="menu">
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              alCambiarAbierto(false);
              alPedirCopiar();
            }}
          >
            Copiar plantilla
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-opciones-plantilla-peligro"
            onClick={() => {
              alCambiarAbierto(false);
              alPedirEliminar();
            }}
          >
            Eliminar
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Subir / bajar / duplicar / eliminar — el mismo cuarteto para cualquier tarjeta. */
function AccionesOrden({
  primero,
  ultimo,
  onSubir,
  onBajar,
  onDuplicar,
  onEliminar,
}: {
  primero: boolean;
  ultimo: boolean;
  onSubir: () => void;
  onBajar: () => void;
  onDuplicar: () => void;
  onEliminar: () => void;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      <button type="button" className="arbol-boton" onClick={onSubir} disabled={primero} aria-label="Subir">
        ↑
      </button>
      <button type="button" className="arbol-boton" onClick={onBajar} disabled={ultimo} aria-label="Bajar">
        ↓
      </button>
      <button type="button" className="arbol-boton" onClick={onDuplicar} aria-label="Duplicar" title="Duplicar">
        ⧉
      </button>
      <button type="button" className="arbol-boton" onClick={onEliminar} aria-label="Eliminar">
        ×
      </button>
    </div>
  );
}

function TarjetaEncabezado({
  item,
  activo,
  editable,
  primero,
  ultimo,
  onSubir,
  onBajar,
  onEliminar,
  onDuplicar,
  onEditar,
  onSeleccionar,
  resaltado,
}: PropsItemComun): JSX.Element {
  if (!activo) {
    return (
      <div
        role="button"
        tabIndex={0}
        className={`panel item-procedimiento item-procedimiento-colapsado${resaltado ? ' item-resaltado' : ''}`}
        onClick={onSeleccionar}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSeleccionar();
          }
        }}
      >
        <div style={{ padding: 14, fontWeight: 700, fontSize: 15 }}>{item.nombre || 'Nuevo encabezado'}</div>
      </div>
    );
  }

  return (
    <div className={`panel item-procedimiento activo${resaltado ? ' item-resaltado' : ''}`}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: 14 }}>
        <input
          value={item.nombre}
          disabled={!editable}
          onChange={(e) => onEditar({ nombre: e.target.value })}
          onBlur={() => {
            if (item.nombre !== item.nombre.trim()) onEditar({ nombre: item.nombre.trim() });
          }}
          placeholder="Título del encabezado"
          autoFocus
          aria-invalid={!item.nombre.trim()}
          style={{
            flex: 1,
            fontWeight: 700,
            fontSize: 15,
            ...(item.nombre.trim() ? {} : { outline: '2px solid var(--falla)' }),
          }}
        />
        {editable ? (
          <AccionesOrden
            primero={primero}
            ultimo={ultimo}
            onSubir={onSubir}
            onBajar={onBajar}
            onDuplicar={onDuplicar}
            onEliminar={onEliminar}
          />
        ) : null}
      </div>
    </div>
  );
}

function TarjetaSeccion({
  item,
  activo,
  editable,
  primero,
  ultimo,
  onSubir,
  onBajar,
  onEliminar,
  onDuplicar,
  onEditar,
  onSeleccionar,
  resaltado,
  arrastrable,
  onIniciarArrastre,
  children,
}: PropsItemComun & {
  arrastrable: boolean;
  onIniciarArrastre: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={`panel item-procedimiento seccion${activo ? ' activo' : ''}${resaltado ? ' item-resaltado' : ''}`}>
      <div
        className="panel-cabecera"
        onClick={activo ? undefined : onSeleccionar}
        style={activo ? undefined : { cursor: editable ? 'pointer' : 'default' }}
      >
        {arrastrable ? (
          <span
            className="seccion-agarre"
            aria-hidden="true"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onIniciarArrastre();
            }}
            title="Arrastrar para reordenar"
          >
            <IconoArrastrar />
          </span>
        ) : null}
        {activo ? (
          <input
            value={item.nombre}
            disabled={!editable}
            onChange={(e) => onEditar({ nombre: e.target.value })}
          onBlur={() => {
            if (item.nombre !== item.nombre.trim()) onEditar({ nombre: item.nombre.trim() });
          }}
            placeholder="Nombre de la sección"
            autoFocus
            aria-invalid={!item.nombre.trim()}
            style={{
              flex: 1,
              fontWeight: 700,
              border: 'none',
              background: 'none',
              fontSize: 14,
              padding: 0,
              ...(item.nombre.trim() ? {} : { outline: '2px solid var(--falla)', borderRadius: 4 }),
            }}
          />
        ) : (
          <span style={{ flex: 1, fontWeight: 700, fontSize: 14 }}>{item.nombre || 'Nueva sección'}</span>
        )}
        {editable && activo ? (
          <AccionesOrden
            primero={primero}
            ultimo={ultimo}
            onSubir={onSubir}
            onBajar={onBajar}
            onDuplicar={onDuplicar}
            onEliminar={onEliminar}
          />
        ) : null}
      </div>
      <div className="panel-cuerpo">{children}</div>
    </div>
  );
}

function TarjetaProcedimientoAnidado({
  item,
  activo,
  editable,
  primero,
  ultimo,
  onSubir,
  onBajar,
  onEliminar,
  onDuplicar,
  onSeleccionar,
  resaltado,
}: PropsItemComun): JSX.Element {
  return (
    <div
      className={`panel item-procedimiento${activo ? ' activo' : ''}${resaltado ? ' item-resaltado' : ''}`}
      role={activo ? undefined : 'button'}
      tabIndex={activo ? undefined : 0}
      onClick={activo ? undefined : onSeleccionar}
      onKeyDown={
        activo
          ? undefined
          : (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSeleccionar();
              }
            }
      }
      style={activo ? undefined : { cursor: 'pointer' }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: 14 }}>
        <span className="arbol-tipo anidado">{ETIQUETA_TIPO_ITEM_PLANTILLA.PROCEDIMIENTO_ANIDADO}</span>
        <span style={{ flex: 1, fontWeight: 600 }}>{item.nombre}</span>
        {editable && activo ? (
          <AccionesOrden
            primero={primero}
            ultimo={ultimo}
            onSubir={onSubir}
            onBajar={onBajar}
            onDuplicar={onDuplicar}
            onEliminar={onEliminar}
          />
        ) : null}
      </div>
    </div>
  );
}

/** Mismos colores que el semáforo real de la ejecución (§`.semaforo` en
 * app.css): la previsualización tiene que anticipar lo que el técnico va a
 * ver, no un botón genérico igual para los tres resultados. */
const COLOR_RESULTADO_INSPECCION: Record<(typeof RESULTADOS_INSPECCION)[number], string> = {
  APROBADO: '--exito',
  ALERTA: '--alerta',
  FALLA: '--falla',
};

/** El código que se guarda («SI»/«NO»/«NA») no es lo que se lee en pantalla. */
const ETIQUETA_RESPUESTA_TERNARIA: Record<(typeof RESPUESTAS_TERNARIAS)[number], string> = {
  SI: 'Sí',
  NO: 'No',
  NA: 'N/A',
};

/** Previsualización del tipo de respuesta, para que se entienda qué va a ver
 * el técnico sin tener que ejecutar el procedimiento. */
function PrevisualizacionCampo({
  tipoCampo,
  opciones,
  nombre,
}: {
  tipoCampo: TipoCampoProcedimiento;
  opciones: string[];
  /** Nombre real del campo — la casilla lo usa como su propia etiqueta, no un texto fijo. */
  nombre?: string;
}): JSX.Element {
  switch (tipoCampo) {
    case 'CASILLA':
      return (
        <label className="previsualizacion-casilla">
          <input type="checkbox" disabled /> {nombre || 'Casilla'}
        </label>
      );
    case 'NUMERICO':
      return <input disabled placeholder="0" className="previsualizacion-campo-corto" />;
    case 'IMPORTE':
      return <input disabled placeholder="S/ 0.00" className="previsualizacion-campo-corto" />;
    case 'FECHA':
      // Un `<input type="date">` deshabilitado se ve distinto en cada
      // navegador (el selector nativo, con su propio look) — acá lo que
      // importa es anticipar el formato, no el widget real del sistema.
      return (
        <div className="previsualizacion-fecha">
          <IconoCalendario />
          dd/mm/aaaa
        </div>
      );
    case 'FIRMA':
      return <div className="previsualizacion-marcador">Firma dibujada en pantalla con el dedo</div>;
    case 'IMAGEN_ARCHIVO':
      return <div className="previsualizacion-marcador">Agregue o arrastre imágenes</div>;
    case 'SI_NO_NA':
      return (
        <div className="previsualizacion-segmentada">
          {RESPUESTAS_TERNARIAS.map((r) => (
            <span key={r} className="pildora-respuesta">
              {ETIQUETA_RESPUESTA_TERNARIA[r]}
            </span>
          ))}
        </div>
      );
    case 'INSPECCION':
      return (
        <div className="previsualizacion-segmentada">
          {RESULTADOS_INSPECCION.map((r) => (
            <span
              key={r}
              className="pildora-respuesta"
              style={{
                color: `var(${COLOR_RESULTADO_INSPECCION[r]})`,
                borderColor: `var(${COLOR_RESULTADO_INSPECCION[r]})`,
              }}
            >
              {r}
            </span>
          ))}
        </div>
      );
    case 'ELECCION_MULTIPLE':
    case 'LISTA_COMPROBACION': {
      // La fila vacía del final (§ pedido: para seguir escribiendo sin un
      // clic aparte) es un detalle del editor, no una opción real todavía —
      // la previsualización no debe mostrar un checkbox sin nombre por ella.
      const reales = opciones.map((o) => o.trim()).filter(Boolean);
      if (reales.length === 0) {
        return <span className="previsualizacion-vacia">Sin opciones todavía.</span>;
      }
      return (
        <div className="previsualizacion-opciones">
          {reales.map((o, i) => (
            <label key={i} className="previsualizacion-opcion">
              <input type={tipoCampo === 'ELECCION_MULTIPLE' ? 'radio' : 'checkbox'} disabled />
              <span>{o}</span>
            </label>
          ))}
        </div>
      );
    }
    case 'LECTURA_MEDIDOR':
      return <input disabled placeholder="Lectura del medidor" />;
    case 'TEXTO':
    default:
      return <textarea disabled placeholder="El texto se ingresará aquí" className="previsualizacion-texto" />;
  }
}

/**
 * Opciones de «Elección múltiple» / «Lista de comprobación», una fila por
 * opción en vez de un textarea «una por línea»: se ve y se edita cada opción
 * donde va a aparecer, se reordenan sin reescribir el texto entero, y la fila
 * Enter en la última fila (o «Añadir opción») agrega la siguiente — nunca
 * de una sola escritura, para no ir dejando filas vacías atrás. Una que se
 * agrega y nunca se completa la descarta `aEntrada` al guardar (§ pedido).
 */
function EditorOpciones({
  opciones,
  editable,
  onCambiar,
}: {
  opciones: string[];
  editable: boolean;
  onCambiar: (opciones: string[]) => void;
}): JSX.Element {
  const filas = opciones.length > 0 ? opciones : [''];

  const escribir = (indice: number, valor: string): void => {
    const siguiente = [...filas];
    siguiente[indice] = valor;
    onCambiar(siguiente);
  };

  const agregarDesde = (indice: number): void => {
    if (indice === filas.length - 1) onCambiar([...filas, '']);
  };

  const quitar = (indice: number): void => {
    const siguiente = filas.filter((_, i) => i !== indice);
    onCambiar(siguiente.length > 0 ? siguiente : ['']);
  };

  const [arrastrando, setArrastrando] = useState<number | null>(null);

  const soltar = (destino: number): void => {
    if (arrastrando === null || arrastrando === destino) return;
    const siguiente = [...filas];
    const [movido] = siguiente.splice(arrastrando, 1);
    siguiente.splice(destino, 0, movido!);
    onCambiar(siguiente);
    setArrastrando(null);
  };

  return (
    <div className="editor-opciones">
      {filas.map((valor, i) => (
        <div
          key={i}
          className={`editor-opciones-fila${arrastrando === i ? ' arrastrando' : ''}`}
          onDragOver={(e) => editable && arrastrando !== null && e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            soltar(i);
          }}
        >
          <span
            className="editor-opciones-agarre"
            aria-hidden="true"
            draggable={editable}
            onDragStart={() => setArrastrando(i)}
            onDragEnd={() => setArrastrando(null)}
          >
            ⠿⠿
          </span>
          <textarea
            className="editor-opciones-texto"
            rows={1}
            style={{ height: 38, maxHeight: 38 }}
            value={valor}
            disabled={!editable}
            onChange={(e) => escribir(i, e.target.value)}
            onBlur={() => {
              if (valor !== valor.trim()) escribir(i, valor.trim());
            }}
            onKeyDown={(e) => {
              // Enter agrega la siguiente opción, no un salto de línea dentro
              // de esta: una opción es una sola idea, no un párrafo.
              if (e.key === 'Enter') {
                e.preventDefault();
                agregarDesde(i);
              }
            }}
            placeholder={`Opción ${i + 1}`}
          />
          {editable && filas.length > 1 ? (
            <button type="button" className="arbol-boton" onClick={() => quitar(i)} aria-label="Quitar opción">
              ×
            </button>
          ) : null}
        </div>
      ))}
      {editable ? (
        <button type="button" className="boton-enlace" onClick={() => onCambiar([...filas, ''])}>
          + Añadir opción
        </button>
      ) : null}
    </div>
  );
}


/** Etiquetas de condición para Casilla: el valor guardado es «true»/«false»,
 * pero el editor debe leerse como «marcado»/«sin marcar» (§8.5, doc MaintainX). */
const ETIQUETA_VALOR_CONDICION_CASILLA: Record<string, string> = {
  true: 'marcado',
  false: 'sin marcar',
};

const ETIQUETA_OPERADOR_CONDICION: Record<OperadorCondicion, string> = {
  UNO_DE: 'uno de',
  NINGUNO_DE: 'ninguno de',
  NO_SELECCIONADO: 'no seleccionado',
  MAYOR_QUE: 'supera',
  MENOR_QUE: 'inferior a',
  IGUAL_A: 'igual a',
  DISTINTO_A: 'distinto a',
  ENTRE: 'entre',
};

/** Operadores que comparan contra uno o dos números, en vez de una lista de valores. */
const OPERADORES_UMBRAL: readonly OperadorCondicion[] = ['MAYOR_QUE', 'MENOR_QUE', 'IGUAL_A', 'DISTINTO_A', 'ENTRE'];

/** Etiqueta legible de un valor de condición, según el tipo del campo disparador. */
function etiquetaValorCondicion(valor: string, tipoCampoPadre: TipoCampoProcedimiento | null): string {
  if (tipoCampoPadre === 'CASILLA') return ETIQUETA_VALOR_CONDICION_CASILLA[valor] ?? valor;
  if (tipoCampoPadre === 'SI_NO_NA') {
    return ETIQUETA_RESPUESTA_TERNARIA[valor as keyof typeof ETIQUETA_RESPUESTA_TERNARIA] ?? valor;
  }
  return valor;
}

/**
 * Bloque "Si respuesta es [operador] [valores] 🗑" (§8.5, doc MaintainX): edita
 * la condición de un campo hijo respecto a la respuesta de su padre directo.
 * Casilla es un disparador binario (sin segundo selector de valores); el resto
 * ofrece "uno de"/"ninguno de" (con checkboxes de las opciones del padre) o
 * "no seleccionado" (sin valores, se cumple si el padre no fue respondido).
 */
/**
 * Conector visual entre el campo disparador y su campo condicional (§8.5,
 * paridad con el bloque "Si respuesta es…" de MaintainX): un riel con icono y
 * línea a la izquierda, y a la derecha un panel diferenciado —"Lógica
 * condicional"— con el operador, los valores que activan la rama, y una
 * papelera que retira la rama completa (condición + campo anidado), no solo
 * la condición, porque un campo huérfano sin condición y sin ser raíz no
 * tiene sentido en el árbol.
 */
function ConectorCondicional({
  condicion,
  valoresPosibles,
  valoresLibres,
  tipoCampoPadre,
  editable,
  onCambiar,
  onEliminarRama,
}: {
  condicion: Condicion | undefined;
  valoresPosibles: string[];
  /** Sin opciones discretas (texto, número, fecha…): el autor escribe los valores. */
  valoresLibres: boolean;
  tipoCampoPadre: TipoCampoProcedimiento | null;
  editable: boolean;
  onCambiar: (condicion: Condicion | undefined) => void;
  onEliminarRama: () => void;
}): JSX.Element {
  const idSelector = useId();
  const esCasilla = tipoCampoPadre === 'CASILLA';
  const operador = condicion?.operador ?? 'UNO_DE';
  const operadores = esCasilla ? (['UNO_DE'] as const) : operadoresCondicionPara(tipoCampoPadre);
  const esUmbral = OPERADORES_UMBRAL.includes(operador);
  const cantidadUmbrales = operador === 'ENTRE' ? 2 : 1;

  // Actualiza un número del umbral sin perder el otro (en «entre», los dos extremos).
  function cambiarUmbral(indice: number, texto: string): void {
    const nuevos = Array.from({ length: cantidadUmbrales }, (_, i) => condicion?.valores[i] ?? '');
    nuevos[indice] = texto.trim();
    onCambiar({ operador, valores: nuevos });
  }

  return (
    <div className="conector-condicional">
      <div className="conector-condicional-riel" aria-hidden="true">
        <span className="conector-condicional-icono">
          <IconoCondicional />
        </span>
        <span className="conector-condicional-linea" />
      </div>
      <div className="conector-condicional-cuerpo">
        <div className="conector-condicional-cabecera">
          <span className="conector-condicional-titulo">Lógica condicional</span>
          {editable ? (
            <button
              type="button"
              className="arbol-boton"
              aria-label="Quitar esta condición"
              title="Quitar esta condición"
              onClick={onEliminarRama}
            >
              <IconoEliminar />
            </button>
          ) : null}
        </div>
        <div className="conector-condicional-regla">
          <span className="conector-condicional-si">Si la respuesta es</span>
          <div style={{ minWidth: 150 }}>
            <SelectorLista
              id={idSelector}
              valor={esCasilla ? (condicion?.valores[0] ?? 'true') : operador}
              deshabilitado={!editable}
              requerido
              alCambiar={(v) => {
                if (esCasilla) {
                  onCambiar({ operador: 'UNO_DE', valores: [v] });
                  return;
                }
                const nuevoOperador = v as OperadorCondicion;
                // Los umbrales llevan un número propio: no heredan la lista de valores.
                onCambiar(
                  nuevoOperador === 'NO_SELECCIONADO' || OPERADORES_UMBRAL.includes(nuevoOperador)
                    ? { operador: nuevoOperador, valores: [] }
                    : { operador: nuevoOperador, valores: condicion?.valores ?? [] },
                );
              }}
              opciones={
                esCasilla
                  ? valoresPosibles.map((v) => ({ id: v, nombre: etiquetaValorCondicion(v, tipoCampoPadre) }))
                  : operadores.map((op) => ({ id: op, nombre: ETIQUETA_OPERADOR_CONDICION[op] }))
              }
            />
          </div>
          {esUmbral ? (
            <span className="conector-condicional-umbral">
              <input
                type="number"
                step="any"
                inputMode="decimal"
                aria-label={operador === 'ENTRE' ? 'Desde' : 'Número'}
                value={condicion?.valores[0] ?? ''}
                disabled={!editable}
                placeholder={operador === 'ENTRE' ? 'Desde' : '0'}
                onChange={(e) => cambiarUmbral(0, e.target.value)}
              />
              {operador === 'ENTRE' ? (
                <>
                  <span className="conector-condicional-si">y</span>
                  <input
                    type="number"
                    step="any"
                    inputMode="decimal"
                    aria-label="Hasta"
                    value={condicion?.valores[1] ?? ''}
                    disabled={!editable}
                    placeholder="Hasta"
                    onChange={(e) => cambiarUmbral(1, e.target.value)}
                  />
                </>
              ) : null}
            </span>
          ) : null}
        </div>
        {!esCasilla && operador !== 'NO_SELECCIONADO' && !esUmbral ? (
          valoresLibres ? (
            <ValoresLibres
              valores={(condicion?.valores ?? []).map((v) => v.trim())}
              tipoCampo={tipoCampoPadre}
              soloLectura={!editable}
              alCambiar={(valores) => onCambiar(valores.length > 0 ? { operador, valores } : undefined)}
            />
          ) : (
            <SeleccionMultiple
              etiqueta="Valores que activan la condición"
              opciones={valoresPosibles.map((v) => ({ id: v, nombre: etiquetaValorCondicion(v, tipoCampoPadre) }))}
              seleccion={(condicion?.valores ?? []).map((v) => v.trim())}
              soloLectura={!editable}
              alCambiar={(valores) => onCambiar(valores.length > 0 ? { operador, valores } : undefined)}
            />
          )
        ) : null}
      </div>
    </div>
  );
}

/**
 * Valores de una condición cuando el campo padre no tiene opciones fijas (§8.5):
 * el autor escribe cada valor tal como lo respondería el técnico. Se compara
 * como texto exacto contra la respuesta, así que un número o una fecha deben
 * escribirse en el mismo formato en que se responden.
 */
function ValoresLibres({
  valores,
  tipoCampo,
  soloLectura,
  alCambiar,
}: {
  valores: string[];
  tipoCampo: TipoCampoProcedimiento | null;
  soloLectura: boolean;
  alCambiar: (valores: string[]) => void;
}): JSX.Element {
  const [borrador, setBorrador] = useState('');
  const [error, setError] = useState<string | null>(null);
  const esFecha = tipoCampo === 'FECHA';

  // Misma regla que el guardado (`normalizarValorCondicion`): lo que se acepta
  // acá es exactamente lo que el servidor va a aceptar.
  function agregar(): void {
    const normalizado = normalizarValorCondicion(tipoCampo, borrador);
    if (normalizado === null) {
      setError(esFecha ? 'Elija una fecha válida.' : 'Escriba un valor válido para este campo.');
      return;
    }
    setBorrador('');
    setError(null);
    if (valores.includes(normalizado)) return;
    alCambiar([...valores, normalizado]);
  }

  return (
    <div className="campo">
      <span className="campo-titulo">Valores que activan la condición</span>
      {valores.length === 0 ? <p className="campo-nota">Ninguno todavía.</p> : null}
      {valores.length > 0 ? (
        <div className="valores-libres">
          {valores.map((v) => (
            <span key={v} className="valores-libres-chip">
              {v}
              {soloLectura ? null : (
                <button
                  type="button"
                  className="arbol-boton"
                  aria-label={`Quitar el valor ${v}`}
                  title="Quitar el valor"
                  onClick={() => alCambiar(valores.filter((x) => x !== v))}
                >
                  <IconoEliminar />
                </button>
              )}
            </span>
          ))}
        </div>
      ) : null}
      {error ? (
        <p className="campo-nota" role="alert">
          {error}
        </p>
      ) : null}
      {soloLectura ? null : (
        <div className="valores-libres-alta">
          <input
            type={esFecha ? 'date' : 'text'}
            value={borrador}
            placeholder={esFecha ? 'Elija una fecha' : 'Escriba un valor y presione Enter'}
            onChange={(e) => {
              setBorrador(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                agregar();
              }
            }}
          />
          <button type="button" className="boton-agregar-condicion" onClick={agregar} disabled={borrador.trim() === ''}>
            <IconoMas /> Agregar valor
          </button>
        </div>
      )}
    </div>
  );
}

function TarjetaCampo({
  item,
  activo,
  editable,
  onEliminar,
  onDuplicar,
  onEditar,
  onSeleccionar,
  resaltado,
}: PropsItemComun & { puntuacionActiva: boolean }): JSX.Element {
  const [mostrarImagenes, setMostrarImagenes] = useState(item.imagenesReferencia.length > 0);
  const tipoCampo = item.tipoCampo ?? 'TEXTO';
  const usaOpciones = tipoCampo === 'ELECCION_MULTIPLE' || tipoCampo === 'LISTA_COMPROBACION';

  // Colapsado por omisión (§ pedido: solo un formato, en vez de siempre el
  // editor completo abierto): se expande al seleccionarlo y vuelve a
  // colapsarse en cuanto se selecciona cualquier otro ítem.
  if (!activo) {
    return (
      // Contiene controles de previsualización (inputs/textareas deshabilitados):
      // no puede ser un <button> real, que no admite contenido interactivo anidado.
      <div
        role="button"
        tabIndex={0}
        className={`panel item-procedimiento item-procedimiento-colapsado campo-tarjeta${resaltado ? ' item-resaltado' : ''}`}
        onClick={onSeleccionar}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSeleccionar();
          }
        }}
      >
        <div className="panel-cuerpo">
          <div className="campo-tarjeta-etiquetas">
            <span className="campo-tarjeta-badge">{ETIQUETA_TIPO_CAMPO_PROCEDIMIENTO[tipoCampo]}</span>
            {item.requerido ? <span className="campo-tarjeta-requerido-chip">Requerido</span> : null}
          </div>
          {/* La casilla ya lleva el nombre como su propia etiqueta (ver más abajo):
              repetirlo arriba sería mostrarlo dos veces. */}
          {tipoCampo !== 'CASILLA' ? (
            <span
            className="item-procedimiento-colapsado-titulo"
            style={item.nombre.trim() ? undefined : { color: 'var(--falla)' }}
          >
            {item.nombre.trim() || 'Falta el nombre del campo'}
          </span>
          ) : null}
          <PrevisualizacionCampo tipoCampo={tipoCampo} opciones={item.opciones} nombre={item.nombre} />
        </div>
      </div>
    );
  }

  return (
    <div className={`panel item-procedimiento activo campo-tarjeta${resaltado ? ' item-resaltado' : ''}`}>
      <div className="campo-tarjeta-cabecera">
        <input
          value={item.nombre}
          disabled={!editable}
          onChange={(e) => onEditar({ nombre: e.target.value })}
          onBlur={() => {
            if (item.nombre !== item.nombre.trim()) onEditar({ nombre: item.nombre.trim() });
          }}
          placeholder="Nombre o pregunta del campo"
          className="campo-tarjeta-nombre"
          aria-invalid={!item.nombre.trim()}
          style={item.nombre.trim() ? undefined : { outline: '2px solid var(--falla)' }}
        />
        <div className="campo-tarjeta-tipo">
          <SelectorLista
            id={`campo-tipo-${item.id}`}
            valor={tipoCampo}
            deshabilitado={!editable}
            requerido
            alCambiar={(v) => onEditar({ tipoCampo: v as TipoCampoProcedimiento })}
            opciones={TIPOS_CAMPO_PROCEDIMIENTO.map((t) => ({ id: t, nombre: ETIQUETA_TIPO_CAMPO_PROCEDIMIENTO[t] }))}
          />
        </div>
      </div>

      <div className="panel-cuerpo campo-tarjeta-cuerpo">
        {/* La casilla ya es su propio nombre + su propio tipo, y las opciones
            ya se ven —y se editan— en el editor de abajo: repetirlas como
            previsualización sería la misma fila una tercera vez. */}
        {tipoCampo !== 'CASILLA' && !usaOpciones ? (
          <PrevisualizacionCampo tipoCampo={tipoCampo} opciones={item.opciones} nombre={item.nombre} />
        ) : null}

        {usaOpciones ? (
          <EditorOpciones
            opciones={item.opciones}
            editable={editable}
            onCambiar={(opciones) => onEditar({ opciones })}
          />
        ) : null}

        {mostrarImagenes ? (
          <div className="campo-tarjeta-imagenes">
            {editable ? (
              <SubidaImagenes
                etiqueta="Imágenes de referencia"
                valor={item.imagenesReferencia}
                alCambiar={(imagenes) => onEditar({ imagenesReferencia: imagenes })}
                maximo={10}
              />
            ) : item.imagenesReferencia.length > 0 ? (
              <GaleriaImagenes fotos={item.imagenesReferencia} />
            ) : null}
          </div>
        ) : null}
      </div>

      {editable ? (
        <div className="item-procedimiento-pie campo-tarjeta-pie">
          <label className="campo-tarjeta-requerido">
            <span>Requerido</span>
            <button
              type="button"
              role="switch"
              aria-checked={item.requerido}
              aria-label="Requerido"
              className="interruptor"
              onClick={() => onEditar({ requerido: !item.requerido })}
            />
          </label>
          <div className="campo-tarjeta-acciones-secundarias">
            <button
              type="button"
              className={`arbol-boton${mostrarImagenes ? ' activo' : ''}`}
              onClick={() => setMostrarImagenes((v) => !v)}
              aria-label="Imágenes de referencia"
              title="Imágenes de referencia"
            >
              <IconoGaleria />
            </button>
            <button type="button" className="arbol-boton" onClick={onDuplicar} aria-label="Duplicar" title="Duplicar">
              ⧉
            </button>
            <button
              type="button"
              className="arbol-boton peligro"
              onClick={onEliminar}
              aria-label="Eliminar"
              title="Eliminar"
            >
              <IconoEliminar />
            </button>
          </div>
        </div>
      ) : item.requerido ? (
        <div className="item-procedimiento-pie campo-tarjeta-pie">
          <span className="campo-tarjeta-requerido-chip">Requerido</span>
        </div>
      ) : null}
    </div>
  );
}

// ─── Historial de ejecuciones (§8.10, punto 28) ─────────────────────────────

const TONO_RESULTADO: Record<'EN_PROGRESO' | 'COMPLETADA', 'ambar' | 'verde'> = {
  EN_PROGRESO: 'ambar',
  COMPLETADA: 'verde',
};

export function HistorialEjecuciones(): JSX.Element {
  const [pagina, setPagina] = useState(1);
  const [estado, setEstado] = useState('');
  const [plantillaId, setPlantillaId] = useState('');
  const [ubicacionId, setUbicacionId] = useState('');
  const [activoId, setActivoId] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  const { data: ubicaciones } = useUbicaciones();
  const { data: plantillas } = useQuery({
    queryKey: ['procedimientos', '', ''],
    queryFn: () => api.get<Plantilla[]>('/procedimientos'),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['ejecuciones', pagina, estado, plantillaId, ubicacionId, activoId, fechaDesde, fechaHasta],
    queryFn: () =>
      api.get<Pagina<EjecucionResumida>>(
        `/ejecuciones${consulta({
          pagina,
          tamano: 25,
          estado,
          plantillaId,
          ubicacionId,
          activoId,
          fechaDesde,
          fechaHasta,
        })}`,
      ),
  });

  const cantidadFiltros = [ubicacionId, activoId, fechaDesde, fechaHasta].filter(Boolean).length;

  const limpiarFiltrosAvanzados = (): void => {
    setUbicacionId('');
    setActivoId('');
    setFechaDesde('');
    setFechaHasta('');
    setPagina(1);
  };

  return (
    <>
      <BotonVolver a="/procedimientos" etiqueta="Volver a Procedimientos" />

      <div className="cabecera-pagina">
        <div>
          <h1>Historial de ejecuciones</h1>
          <p>Todos los checklists ejecutados, de cualquier orden de trabajo.</p>
        </div>
      </div>

      <div className="rejilla-indicadores">
        <Indicador titulo="EJECUCIONES EN LA VISTA" valor={data?.total ?? '—'} tono="azul" />
        <Indicador
          titulo="EN PROGRESO EN LA PÁGINA"
          valor={data?.datos.filter((e) => e.estado === 'EN_PROGRESO').length ?? 0}
          tono="azul"
        />
        <Indicador
          titulo="CON FALLAS EN LA PÁGINA"
          valor={data?.datos.filter((e) => e.resumen.falla > 0).length ?? 0}
          tono="roja"
        />
      </div>

      <div className="filtros">
        <SelectorLista
          id="ejecuciones-filtro-estado"
          etiqueta="Estado"
          valor={estado}
          alCambiar={(v) => {
            setEstado(v);
            setPagina(1);
          }}
          opciones={[
            { id: 'EN_PROGRESO', nombre: 'En progreso' },
            { id: 'COMPLETADA', nombre: 'Completada' },
          ]}
          textoVacio="Todos los estados"
        />
        <SelectorLista
          id="ejecuciones-filtro-plantilla"
          etiqueta="Plantilla"
          valor={plantillaId}
          alCambiar={(v) => {
            setPlantillaId(v);
            setPagina(1);
          }}
          opciones={(plantillas ?? []).map((p) => ({ id: p.id, nombre: p.nombre }))}
          textoVacio="Todas las plantillas"
        />
      </div>

      <details className="panel filtros-avanzados" style={{ marginBottom: 16 }}>
        <summary>Más filtros{cantidadFiltros > 0 ? ` (${cantidadFiltros})` : ''}</summary>
        <div className="panel-cuerpo">
          <div className="rejilla-campos">
            <SelectorUbicacion
              id="ejecuciones-filtro-ubicacion"
              valor={ubicacionId}
              alCambiar={(v) => {
                setUbicacionId(v);
                setActivoId('');
                setPagina(1);
              }}
              ubicaciones={ubicaciones}
            />
            <SelectorActivo
              id="ejecuciones-filtro-activo"
              valor={activoId}
              alCambiar={(v) => {
                setActivoId(v);
                setPagina(1);
              }}
              ubicacionId={ubicacionId || undefined}
            />
            <SelectorFecha
              id="ejecuciones-filtro-desde"
              etiqueta="Fecha (desde)"
              valor={fechaDesde}
              alCambiar={(v) => {
                setFechaDesde(v);
                setPagina(1);
              }}
            />
            <SelectorFecha
              id="ejecuciones-filtro-hasta"
              etiqueta="Fecha (hasta)"
              valor={fechaHasta}
              alCambiar={(v) => {
                setFechaHasta(v);
                setPagina(1);
              }}
            />
          </div>
          {cantidadFiltros > 0 ? (
            <button type="button" className="boton secundario pequeno" onClick={limpiarFiltrosAvanzados}>
              Limpiar filtros
            </button>
          ) : null}
        </div>
      </details>

      <div className="panel">
        {isLoading ? <Cargando /> : null}
        {data && data.datos.length === 0 ? (
          <Vacio titulo="Sin ejecuciones" detalle="Ajuste los filtros o adjunte un procedimiento a una OT." />
        ) : null}

        {data && data.datos.length > 0 ? (
          <>
            <div className="tabla-contenedor">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Plantilla</th>
                    <th>OT</th>
                    <th>Ubicación</th>
                    <th>Activo</th>
                    <th>Estado</th>
                    <th>Resultado</th>
                    <th>Iniciada</th>
                    <th>Completada</th>
                  </tr>
                </thead>
                <tbody>
                  {data.datos.map((e) => (
                    <tr key={e.id}>
                      <td>
                        <EnlaceRuta to={`/ejecuciones/${e.id}`} style={{ fontWeight: 600 }}>
                          {e.plantillaNombre}
                        </EnlaceRuta>
                        <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>v{e.plantillaVersion}</div>
                      </td>
                      <td>
                        <EnlaceRuta to={`/ordenes/${e.ordenTrabajoId}`}>#{e.ordenTrabajoNumero}</EnlaceRuta>
                      </td>
                      <td>{e.ubicacion ?? '—'}</td>
                      <td>{e.activo ?? '—'}</td>
                      <td>
                        <Etiqueta tono={TONO_RESULTADO[e.estado]}>
                          {e.estado === 'COMPLETADA' ? 'Completada' : 'En progreso'}
                        </Etiqueta>
                      </td>
                      <td>
                        {e.resumen.falla > 0 ? `${e.resumen.falla} falla(s)` : ''}
                        {e.resumen.falla > 0 && e.resumen.alerta > 0 ? ' · ' : ''}
                        {e.resumen.alerta > 0 ? `${e.resumen.alerta} alerta(s)` : ''}
                        {e.resumen.falla === 0 && e.resumen.alerta === 0 ? '—' : ''}
                        {e.porcentaje ? ` · ${e.porcentaje} %` : ''}
                      </td>
                      <td>{formatearFechaHora(e.iniciadoEl)}</td>
                      <td>{e.completadoEl ? formatearFechaHora(e.completadoEl) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Paginacion pagina={data.pagina} tamano={data.tamano} total={data.total} onCambiar={setPagina} />
          </>
        ) : null}
      </div>
    </>
  );
}
